import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAdventurePack } from './adventures';
import { resolveResponse } from './audio/responseStore';
import { startCapture, encodeWav, bytesToB64, int16ToB64, unlockAudio, getAudioCtx } from './live/liveAudio';
import { LiveSTT } from './live/liveSTT';

/**
 * DemoApp - Little Sponges English ESL Tutor (Farm Adventure)
 *
 * Flow per card (conversational deepening):
 *   1. "What is this?" → child says animal name
 *   2. If correct on first try → "What color is the [animal]?" → child says color
 *   3. If correct on first try → "How many [animals] do you see?" → child says number
 *   Then advance to next card.
 *
 * After 5 cards: open-ended "What is your favorite animal?"
 *
 * Voice: Pre-recorded ElevenLabs Motivational Buddy with API fallback
 * Evaluation: Local fuzzy word matching (instant, no API lag)
 * STT: Web Speech API (Chrome)
 */

// ─── Config ───
// Where the Node backend (Lambda + API Gateway) lives.
//   • Production build → the API Gateway URL (CORS handled by the backend).
//   • Local dev → http://localhost:3001 (run `npm run server`).
//   • Override anytime with VITE_API_BASE at build time.
const API_BASE = import.meta.env.VITE_API_BASE
  ?? (import.meta.env.PROD ? 'https://45pfymh0uk.execute-api.us-east-1.amazonaws.com' : 'http://localhost:3001');
const MAX_ATTEMPTS = 3;
const QUESTIONS_PER_GAME = 10;

// AI mode — Gemini for listening + thinking + dynamic Puck speaking.
// DEFAULT ON since 2026-07-29 (Natalya approved the full AI version as the
// production default). Escape hatch: ?ai=0 reverts to the instant local path
// (fuzzy match + pre-baked audio) — keep as the zero-risk rollback knob.
const AI_MODE = typeof window === 'undefined' || new URLSearchParams(window.location.search).get('ai') !== '0';

// STT mode — which ears the app uses (2026-07-02, per Natalya's cross-browser
// mandate: the app must hear kids on ANY browser, not just desktop Chrome).
//   'live'    (default) streaming Gemini Live STT via ephemeral token; falls
//             back to turn-based /api/listen automatically, then Web Speech.
//   'turn'    skip streaming; record the turn → /api/listen (server STT).
//   'hybrid'  the 06-22 design: browser Web Speech primary + Gemini rescue.
//   'browser' original Chrome-only Web Speech path (escape hatch / A-B).
// Override per session with ?stt=turn etc. — zero-risk rollback knob.
const STT_MODE = (typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('stt')) || 'live';

// ─── Active adventure (2026-08-07) ───
// The engine is adventure-agnostic; everything specific to one adventure
// (card pool, deck algorithm, background, STT vocab, whether the favorite
// flow runs) lives in a content pack under src/adventures/. Selected via
// ?adventure=<id>; missing/unknown ids fall back to Farm so every existing
// link — including the WordPress lesson embeds — behaves exactly as before.
const ADVENTURE = getAdventurePack(
  typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('adventure') : null,
  // ?lang= selects a language-variant pack when one exists (farm-es, 2026-08-12).
  // WP lesson links carry no lang param → English, exactly as before.
  typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('lang') : null,
);

// ─── Language layer (2026-08-12, Spanish track) ───
// A pack may carry its own strings: q()/sentence()/correctLine()/revealLine()
// builders, universal `lines`, ui labels, number words, STT/judge language.
// Every accessor below falls back to the original English behavior, so packs
// without a language layer (farm, playground) are byte-for-byte unchanged.
const PACK_LINES = ADVENTURE.lines || {};
const PACK_UI = ADVENTURE.ui || {};
const packLine = (key, defAudio, defText) => PACK_LINES[key] || { audio: defAudio, text: defText };
const uiText = (key, defText) => (typeof PACK_UI[key] === 'string' ? PACK_UI[key] : defText);
const packQ = (card, tier) => (ADVENTURE.q ? ADVENTURE.q(card, tier) : null);
const packSentence = (card, tier) => (ADVENTURE.sentence ? ADVENTURE.sentence(card, tier) : null);
const packCorrect = (card, tier) => (ADVENTURE.correctLine ? ADVENTURE.correctLine(card, tier) : null);
const packReveal = (card, tier) => (ADVENTURE.revealLine ? ADVENTURE.revealLine(card, tier) : null);
// Sent with judge calls so the evaluators grade against the right target
// language ('English' when absent — the original behavior).
const TARGET_LANGUAGE = ADVENTURE.targetLanguage || 'English';

// v4 flow: echoing the sentence at the FINAL "repeat after me" step DOES earn
// the star — Natalya's call (Option A, 2026-07-07): every child who produces
// the sentence ends the card with a win, even with the frog's help.
const STAR_ON_FINAL_ECHO = true;

// Per-turn AI latency breakdown (ms). Written by each phase, shown in the on-screen
// TimingOverlay AND shipped to the session diagnostics via logEvent('ai-timing').
const aiTimings = { record: 0, stt: 0, think: 0, tts: 0, total: 0, turnStart: 0 };
function resetTimings() {
  aiTimings.record = aiTimings.stt = aiTimings.think = aiTimings.tts = aiTimings.total = 0;
  aiTimings.turnStart = (typeof performance !== 'undefined' ? performance.now() : Date.now());
}

// ─── Card pool (from the active adventure's content pack) ───
// Farm's pool + the rationale for its multi-animal cards moved to
// src/adventures/farm.js (2026-08-07). Kept under the ANIMAL_POOL name so the
// rest of the engine reads unchanged — for non-animal adventures these are
// simply "cards".
const ANIMAL_POOL = ADVENTURE.pool;

// ─── Open-ended finale ("favorite") config — generalized 2026-08-11 ───
// Each adventure defines its own closing question via ADVENTURE.favorite
// (question, ask audio, screen image, judge kind, match options, extras,
// synonyms, miss/hint lines). Farm's original values live in farm.js; the
// FALLBACKS below preserve the legacy Farm behavior for any pack that sets
// hasFavorite without a favorite config.
// Garden (2026-08-18): ADVENTURE.favorite may be a FUNCTION — resolved fresh
// on every read, so a pack can flip the finale per session (favorite fruit vs
// favorite vegetable, the opposite of its "Do you like…?" slot). Static packs
// (Farm/Playground/farm-es) return the same object every time — unchanged.
const favConf = () => {
  const f = typeof ADVENTURE.favorite === 'function' ? ADVENTURE.favorite() : ADVENTURE.favorite;
  return f || null;
};
const favQuestion = () => favConf()?.question || 'What is your favorite farm animal?';
const favAskAudio = () => favConf()?.askAudio || '/audio/favorite-ask.mp3';
// The pool the local matcher + judge vocabulary draw from. Farm omits
// `options` → the card pool itself (all 9 animals have pre-baked celebration
// audio). Playground restricts it to actual toys (family cards are not toys).
const favoritePool = () => favConf()?.options || ANIMAL_POOL;

// Tier 1 question variants — randomly picked per card.
// NOTE: only what-is-this.mp3 currently exists on disk. Add the other
// variant entries back to each array once the matching audio files are
// recorded (what-animal-is-this.mp3, what-animal-do-you-see.mp3,
// what-bird-is-this.mp3, what-bird-do-you-see.mp3).
const NAME_QUESTIONS = {
  animal: [
    { audio: '/audio/what-is-this.mp3', text: 'What is this?' },
  ],
  bird: [
    { audio: '/audio/what-is-this.mp3', text: 'What is this?' },
  ],
};

// Known farm-animal words sent to the favorite judge as its CORE vocabulary
// (pool + curated extras — defined below). The judge maps in-vocabulary
// answers to these exact words but may also approve farm animals beyond the
// list (unshackled contract, 2026-07-12).
// NOTE: initialized after FAVORITE_EXTRAS is defined; kept as a function to
// avoid re-ordering this file's declaration blocks.
const getAllAnimalWords = () => [
  ...favoritePool().map((a) => a.word),
  ...favExtras().map((e) => e.word),
];

// Group image used on the final "favorite" screen. Packs supply their own via
// favorite.image; the Farm collage is the fallback.
const FARM_ANIMALS_IMAGE = '/images/unit1/All-Farm-Animals.jpg';
const favImage = () => favConf()?.image || FARM_ANIMALS_IMAGE;

// ─── Favorite-question vocabulary (2026-07-12, Natalya's feedback) ───
// The favorite question is open-ended and the collage on that screen shows
// animals beyond the 9-card pool (quail, goats, ...). Three layers:
//   1. FAVORITE_EXTRAS — broad curated farm animals matched instantly locally.
//      They celebrate with a live Puck line that NAMES the animal ("I love
//      quails too") — no pre-baked file, synthesized once + S3-cached.
//   2. FAVORITE_SYNONYMS — baby/alt names mapped to a pool word, so the child
//      hears that animal's own cached celebration line.
//   3. Anything else → the Gemini judge decides farm/not-farm on the fly
//      (/api/evaluate-favorite, unshackled from the fixed options list);
//      accepted animals are also named via the live Puck line.
// (2026-07-16, Natalya: the celebration must name the animal, never the
// generic "I love that animal too".)
// Expansion is human-in-the-loop: judge approvals ship in the session
// diagnostics (favorite-judge events) — review periodically and promote
// frequent answers into these lists. Do NOT auto-write-back from the AI.
// Both lists moved into the packs (2026-08-11): farm.js carries the original
// farm extras/synonyms verbatim; playground.js carries toy equivalents.
// (Farm's old inline note still applies: no 'ox' in extras — fuzzyMatch's
// substring rule would match it inside "fox"/"box".)
const favExtras = () => favConf()?.extras || [];
const favSynonyms = () => favConf()?.synonyms || {};

// Common animals kids might name that are NOT farm animals.
// Used to differentiate "that's not a farm animal" from "that's not an
// animal" on the final favorite question.
// NOTE (2026-07-12): bunny/llama/alpaca removed — they're accepted farm
// answers now (see FAVORITE_EXTRAS / FAVORITE_SYNONYMS above).
// A language pack may supply its own list (farm-es: perro, gato, león, ...).
const COMMON_ANIMALS = ADVENTURE.commonAnimals || [
  'dog', 'puppy', 'cat', 'kitten', 'kitty',
  'lion', 'tiger', 'cheetah', 'leopard', 'jaguar', 'panther',
  'elephant', 'giraffe', 'zebra', 'rhino', 'rhinoceros', 'hippo', 'hippopotamus',
  'monkey', 'gorilla', 'chimp', 'chimpanzee', 'ape', 'orangutan',
  'panda', 'bear', 'polar bear', 'grizzly', 'koala', 'kangaroo', 'wallaby',
  'wolf', 'coyote', 'fox', 'deer', 'moose', 'elk', 'reindeer',
  'raccoon', 'squirrel', 'chipmunk', 'hamster', 'gerbil', 'mouse', 'rat',
  'bat', 'hedgehog', 'skunk', 'beaver', 'otter', 'seal', 'walrus',
  'owl', 'eagle', 'hawk', 'falcon', 'parrot', 'cockatoo',
  'penguin', 'flamingo', 'peacock', 'crow', 'raven', 'dove', 'pigeon',
  'sparrow', 'robin', 'hummingbird', 'bluebird', 'woodpecker', 'bird',
  'fish', 'shark', 'whale', 'dolphin', 'octopus', 'squid', 'jellyfish',
  'crab', 'lobster', 'shrimp', 'seahorse', 'starfish',
  'turtle', 'tortoise', 'frog', 'toad', 'snake', 'lizard', 'gecko',
  'alligator', 'crocodile', 'iguana', 'dinosaur', 't-rex', 'dragon', 'unicorn',
  'spider', 'bee', 'wasp', 'butterfly', 'moth', 'ant', 'snail',
  'worm', 'ladybug', 'beetle', 'dragonfly', 'caterpillar',
  'camel', 'buffalo', 'bison', 'yak',
];

// Resolve a spoken favorite answer to an animal locally (pool → synonyms →
// extras). Returns { word, plural, generic } or null. generic === true means
// there is no pre-baked audio file (the animal is still named via live Puck).
function matchFavoriteAnimal(alternatives) {
  // allGeneric (Playground): no per-item pre-baked audio exists — every
  // celebration is a live named Puck line (synthesized once, S3-cached).
  const allGeneric = !!favConf()?.allGeneric;
  const pool = favoritePool().find((a) => fuzzyMatchAny(alternatives, [a.word, a.plural]));
  if (pool) return { word: pool.word, plural: pool.plural, generic: allGeneric };
  // Synonyms use EXACT word matching (not fuzzyMatch): short alt names like
  // "hen" would otherwise substring-match inside "when"/"then".
  const spokenWords = new Set(alternatives.flatMap((alt) => cleanSpoken(alt).split(/\s+/)));
  for (const [syn, mappedWord] of Object.entries(favSynonyms())) {
    if (spokenWords.has(syn) || spokenWords.has(`${syn}s`)) {
      const a = favoritePool().find((p) => p.word === mappedWord);
      if (a) return { word: a.word, plural: a.plural, generic: allGeneric };
      const e = favExtras().find((x) => x.word === mappedWord);
      if (e) return { word: e.word, plural: e.plural, generic: true };
    }
  }
  const extra = favExtras().find((e) => fuzzyMatchAny(alternatives, [e.word, e.plural]));
  if (extra) return { word: extra.word, plural: extra.plural, generic: true };
  return null;
}

// Shuffle and pick N
function pickRandom(arr, n) {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

// Build a deck that always totals QUESTIONS_PER_GAME (10) questions.
// Each adventure brings its own algorithm (Farm: 3 full-tier multis topped up
// with name-only cards; Playground: 10 random name-only cards from its 13).
// Implementations live in the adventure packs.
function buildDeck() {
  return ADVENTURE.buildDeck();
}

// ─── Brand Colors (Little Sponges game palette) ───
const BRAND = {
  gold: '#F7AD00',
  goldLight: '#FFC940',
  teal: '#05544B',
  tealLight: '#0A7B6B',
  cyan: '#5BC0DE',
  cyanDark: '#3AAFCF',
  white: '#FFFFFF',
  cream: '#FFF9EC',
  gray: '#728188',
  orange: '#FF6B35',
  orangeDark: '#E55A2B',
  red: '#E74C3C',
  redDark: '#C0392B',
  skyBlue: '#87CEEB',
  grassGreen: '#4CAF50',
  btnGreen: '#5BBF2F',
  btnGreenDark: '#4A9E26',
  hotPink: '#FF4893',
  hotPinkDark: '#D93A7A',
};

const LOGO_SRC = '/images/little-sponges-logo.png';

// English-brand teal (2026-07-07, Natalya): English content uses teal, pink is
// reserved for Spanish. Pulled from the live little-sponges.com English pages:
// bright #0CD3BC (the "video subtitles" turquoise) + deep #05544B (= BRAND.teal).
const ENGLISH_TEAL = '#0CD3BC';
const ENGLISH_TEAL_DARK = '#05544B';
// Per-language accent (2026-10-20, Natalya tracker #13): Spanish content is
// pink across Little Sponges, so the Spanish track's mic button, highlighted
// vocabulary word and Tap-to-Start use the brand hot pink instead of the
// English teal. Other languages fall back to teal until they get their color.
const LANG_ACCENTS = {
  Spanish: [BRAND.hotPink, BRAND.hotPinkDark],
};
const [ACCENT, ACCENT_DARK] = LANG_ACCENTS[TARGET_LANGUAGE] || [ENGLISH_TEAL, ENGLISH_TEAL_DARK];

// ─── Diagnostic event log (always on) ───
// An UPLOAD QUEUE of everything that matters for remote debugging: mic
// lifecycle, recognition results/errors (by name!), match outcomes, audio
// failures. Each event is uploaded exactly once, then removed from the queue,
// so a full game session is captured completely no matter how long it runs.
// (The previous ring-buffer + cursor design silently STOPPED uploading once
//  250 events accumulated — and the per-comparison match-check spam blew past
//  250 in ~8 answers — so long sessions only ever showed a short tail.)
const DIAG_MAX_QUEUE = 5000; // safety ceiling vs. a runaway loop; a normal session is far smaller
const diagLog = [];          // events awaiting upload; fully drained on each flush

// ─── Auto-upload of session logs (telemetry) ───
// The queue is flushed to an API Gateway → Lambda → private S3 bucket in the
// client's AWS account (no third-party vendor): every 20s and when the tab is
// hidden/closed. Set DIAG_ENDPOINT to '' to disable.
const DIAG_ENDPOINT = 'https://ctieq477xf.execute-api.us-east-1.amazonaws.com'; // API Gateway → diag collector Lambda (2026-06-13; public Lambda URLs blocked by org guardrail)
// Optional tester label from the URL (?t=name) so logs can be attributed to a
// person/device. Sanitized, capped at 16 chars. Used as the session prefix, so
// it becomes the S3 folder name — logs auto-sort by tester. e.g. ?t=natalya
const TESTER = (typeof window !== 'undefined')
  ? (new URLSearchParams(window.location.search).get('t') || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 16)
  : '';
// Device class, auto-detected (2026-07-02, Polina's device-zoo request): the
// same ?t=polina link on every machine still yields separate, self-labeled
// S3 folders — polina-iphone-…, polina-ipad-…, polina-mac-…, polina-windows-….
// Note: modern iPadOS Safari masquerades as a Mac; touch points give it away.
function detectDevice() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iphone';
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ipad';
  if (/Android/.test(ua)) return 'android';
  if (/CrOS/.test(ua)) return 'chromebook';
  if (/Windows/.test(ua)) return 'windows';
  if (/Macintosh/.test(ua)) return 'mac';
  return 'other';
}
const DEVICE = detectDevice();
const SESSION_ID = (TESTER ? TESTER + '-' : '') + DEVICE + '-' + Math.random().toString(36).slice(2, 8) + '-' + Date.now().toString(36);
function flushDiag(useBeacon = false) {
  if (!DIAG_ENDPOINT || diagLog.length === 0) return;
  const lines = diagLog.splice(0, diagLog.length); // take all pending events, clear the queue
  const payload = JSON.stringify({
    session: SESSION_ID,
    tester: TESTER,
    ua: navigator.userAgent,
    url: window.location.href,
    lines,
  });
  try {
    const sent = useBeacon && navigator.sendBeacon ? navigator.sendBeacon(DIAG_ENDPOINT, payload) : false;
    if (!sent) {
      fetch(DIAG_ENDPOINT, { method: 'POST', body: payload, keepalive: true })
        .catch(() => { diagLog.unshift(...lines); }); // re-queue on failure so nothing is lost
    }
  } catch { diagLog.unshift(...lines); /* telemetry must never break the app */ }
}
if (typeof window !== 'undefined') {
  setInterval(() => flushDiag(false), 20000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushDiag(true);
  });
}
function logEvent(type, data = '') {
  const t = new Date();
  const stamp = t.toTimeString().slice(0, 8) + '.' + String(t.getMilliseconds()).padStart(3, '0');
  diagLog.push(`${stamp} ${type}${data ? ' ' + (typeof data === 'string' ? data : JSON.stringify(data)) : ''}`);
  if (diagLog.length > DIAG_MAX_QUEUE) diagLog.shift();
}
logEvent('app-load', {
  tester: TESTER || '(none)',
  device: DEVICE,
  ua: typeof navigator !== 'undefined' ? navigator.userAgent : '?',
  screen: typeof window !== 'undefined' ? `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}x` : '?',
  speechRecognition: typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition),
});

// Debug mode: append ?debug to the URL to show what speech recognition heard.
// Invisible to normal users; lets a screen recording capture STT behavior on
// someone else's machine (added 2026-06-10 to diagnose Natalya's misfires).
const DEBUG_STT = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debug');

function SttDebugOverlay({ transcript, alternatives, listening }) {
  if (!DEBUG_STT) return null;
  return (
    <div style={{ position: 'fixed', bottom: 8, left: 8, zIndex: 50, maxWidth: 420,
      background: 'rgba(0,0,0,0.75)', color: '#0f0', fontFamily: 'monospace', fontSize: 13,
      padding: '8px 10px', borderRadius: 8, pointerEvents: 'none', lineHeight: 1.45 }}>
      <div>mic: {listening ? 'LISTENING' : 'off'}</div>
      <div>heard: "{transcript || '—'}"</div>
      <div>alts: {alternatives && alternatives.length ? alternatives.join(' | ') : '—'}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// ─── WORDPRESS INTEGRATION — behind-the-login link + assignment upload
// ════════════════════════════════════════════════════════
// (2026-07-31, built to Jenny's 7/29 spec; upload rerouted 2026-08-05 per her
// 8/3 answers.) WordPress generates the access link for a logged-in student:
//   https://speakingtutor.littlesponges.com/?user_id={id}&lesson_id={id}&token={token}
// Token: HMAC SHA-256 over { uid, lid, iat, exp }, SINGLE-USE, 5-min expiry.
// Score: integer 0-10. Recording: mp3.
// UPLOAD PATH (Jenny 8/3): WP REST keeps CORS closed, so the browser does NOT
// post to her endpoint — it posts to OUR backend (/api/submit-recording,
// base64 JSON like /api/speak), and the Lambda forwards it server-to-server.
// The WordPress domain lives server-side (WP_API_BASE env in server.js).
// PENDING: exact token WIRE format (how payload+signature pack into the token
// string) — known once Jenny's test link arrives; then /api/validate-token
// gets built for session-start validation. Until then the token passes
// through and her endpoint validates on submit.
const WP_LINK = (() => {
  if (typeof window === 'undefined') return { userId: '', lessonId: '', token: '' };
  const q = new URLSearchParams(window.location.search);
  // 2026-08-12: Jenny's embed template had an HTML-escaping bug that glued the
  // anchor markup onto the LAST query param (user_id=61527">Start AI Tutor
  // Session</a>), so WordPress rejected the upload. Defend permanently:
  // (a) keep digits only from the URL params, and (b) prefer the uid/lid inside
  // the SIGNED token payload — template markup can't mangle those.
  const digits = (s) => String(s || '').replace(/\D/g, '');
  const token = (q.get('token') || '').trim();
  let userId = digits(q.get('user_id'));
  let lessonId = digits(q.get('lesson_id'));
  try {
    const p = token.split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(p + '='.repeat((4 - (p.length % 4)) % 4)));
    if (payload && payload.uid != null) userId = digits(payload.uid) || userId;
    if (payload && payload.lid != null) lessonId = digits(payload.lid) || lessonId;
  } catch { /* no token or unparseable payload — sanitized URL params stand */ }
  return { userId, lessonId, token };
})();
if (WP_LINK.token) {
  logEvent('wp-link', { userId: WP_LINK.userId, lessonId: WP_LINK.lessonId, tokenLen: WP_LINK.token.length });
}

// Session-start token check (2026-08-05, format from Jenny's snippet): ask our
// backend to verify the HMAC + expiry ONCE at load, so a kid with an expired
// or mangled link finds out in seconds — not after a full session that can't
// be submitted. Fail-OPEN on network trouble: if the check itself can't run,
// let the game proceed and the WordPress endpoint remains the enforcer.
const IS_WP_LINK = !!(WP_LINK.token || WP_LINK.lessonId || WP_LINK.userId);
const wpTokenCheck = { status: WP_LINK.token ? 'pending' : 'none', reason: '' };
if (WP_LINK.token) {
  fetch(`${API_BASE}/api/validate-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: WP_LINK.token }),
  })
    .then((r) => r.json())
    .then((j) => {
      wpTokenCheck.status = j && j.valid ? 'ok' : 'invalid';
      wpTokenCheck.reason = (j && j.reason) || '';
      logEvent('wp-token-check', { valid: !!(j && j.valid), reason: wpTokenCheck.reason });
    })
    .catch((e) => {
      wpTokenCheck.status = 'ok';
      wpTokenCheck.reason = 'check-unreachable';
      logEvent('wp-token-check-FAIL', { err: e.message });
    });
}

// Full-screen friendly stop screen when the link is bad — mounted on every
// screen (fixed overlay), renders nothing unless the check came back invalid.
function WpLinkGate() {
  const [, force] = useState(0);
  useEffect(() => { const id = setInterval(() => force((n) => n + 1), 400); return () => clearInterval(id); }, []);
  if (wpTokenCheck.status !== 'invalid') return null;
  const headline = wpTokenCheck.reason === 'expired' ? 'This link has expired.' : "This link isn't working.";
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(5,84,75,0.93)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Nunito', sans-serif" }}>
      <div style={{ background: BRAND.white, borderRadius: 24, padding: '36px 44px', maxWidth: 480,
        textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.35)' }}>
        <div style={{ fontSize: 56, marginBottom: 10 }}>🐸</div>
        <div style={{ fontSize: 24, fontWeight: 900, color: BRAND.teal, marginBottom: 8 }}>{headline}</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: BRAND.gray, lineHeight: 1.5 }}>
          Please go back to Little Sponges and open the speaking tutor again to get a fresh link.
        </div>
      </div>
    </div>
  );
}

// Convert whatever the browser recorded (webm/opus, mp4/AAC) to mono mp3.
// Decode via WebAudio, encode via lamejs (patched ESM fork). Runs client-side
// so the Lambda never touches audio transcoding. A 10-minute session encodes
// in a few seconds on a laptop; the UI shows a "Preparing…" state meanwhile.
async function blobToMp3(blob) {
  const lame = await import('@breezystack/lamejs');
  const Mp3Encoder = lame.Mp3Encoder || (lame.default && lame.default.Mp3Encoder);
  if (!Mp3Encoder) throw new Error('mp3 encoder failed to load');
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  try {
    const audio = await ctx.decodeAudioData(await blob.arrayBuffer());
    // Downmix to mono.
    const n = audio.length;
    const mono = new Float32Array(n);
    for (let c = 0; c < audio.numberOfChannels; c++) {
      const ch = audio.getChannelData(c);
      for (let i = 0; i < n; i++) mono[i] += ch[i] / audio.numberOfChannels;
    }
    // Float32 [-1,1] → Int16.
    const samples = new Int16Array(n);
    for (let i = 0; i < n; i++) {
      const s = Math.max(-1, Math.min(1, mono[i]));
      samples[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    // 32kbps mono: plenty for a child's voice, and keeps even an 18-minute
    // session under the Lambda ~6MB request cap on the upload proxy.
    const enc = new Mp3Encoder(1, audio.sampleRate, 32);
    const parts = [];
    const CHUNK = 1152; // lame frame size
    for (let i = 0; i < samples.length; i += CHUNK) {
      const out = enc.encodeBuffer(samples.subarray(i, i + CHUNK));
      if (out.length) parts.push(out);
    }
    const tail = enc.flush();
    if (tail.length) parts.push(tail);
    return new Blob(parts, { type: 'audio/mp3' });
  } finally {
    ctx.close().catch(() => {});
  }
}

// Send the finished recording + score to our backend, which forwards it to
// Jenny's WordPress endpoint server-to-server (no browser CORS — her call,
// 8/3). Base64-over-JSON, matching how the rest of this API speaks.
async function uploadAssignmentRecording(mp3Blob, score) {
  const audioBase64 = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = () => reject(new Error('could not read the recording'));
    r.readAsDataURL(mp3Blob);
  });
  if (!audioBase64) throw new Error('empty recording');
  const res = await fetch(`${API_BASE}/api/submit-recording`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      audioBase64,
      mimeType: 'audio/mp3',
      // Readable timestamp first so every take is distinct and sortable (tracker #1).
      filename: `session-${new Date().toISOString().replace(/[:.]/g, '-')}-${SESSION_ID}.mp3`,
      user_id: WP_LINK.userId,
      lesson_id: WP_LINK.lessonId,
      score: Math.max(0, Math.min(10, Math.round(score || 0))),
      token: WP_LINK.token,
    }),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
  return j;
}

// ════════════════════════════════════════════════════════
// ─── SESSION RECORDER — "Record your session" (2026-07-29)
// ════════════════════════════════════════════════════════
// Teacher/parent-facing button in the bottom-right corner that records the
// child's mic for the whole session, then offers playback + download.
// Module-level singleton (same pattern as diagLog/liveSession) so a recording
// in progress survives screen changes — the button component remounts on every
// screen but reads this shared state.
//
// PENDING DECISIONS (Natalya, asked 2026-07-29):
//   • Manual button vs. auto-record every session. If auto wins, call
//     startSessionRecording() from handleStart and hide the idle button.
//   • This recording is the artifact for Jenny's WordPress
//     /assignment-recording endpoint (multipart `recording` field) once the
//     token flow lands. Format webm-vs-mp3 is an open question with Jenny.
const sessionRec = {
  status: 'idle', // idle | recording | done | error
  mediaRecorder: null,
  stream: null,
  chunks: [],
  blob: null,
  url: '',
  startedAt: 0,
  error: '',
  // Upload-to-teacher state (only meaningful when WP_LINK.token is present):
  uploadStatus: 'idle', // idle | converting | sending | sent | failed
  uploadError: '',
  assignmentId: '',
  // WebAudio mix destination while recording: mic + the frog's own audio both
  // feed this node, so the teacher hears the whole conversation (2026-08-12 —
  // Polina's first live test captured only the child's side).
  mixDest: null,
  exitSaving: false, // ✕ pressed in auto-record mode — upload before leaving
};

// Route one of the app's audio elements into the in-progress recording.
// Called wherever the app creates a playback element. No-op unless a session
// recording is running. createMediaElementSource takes over the element's
// output, so ALWAYS reconnect it to ctx.destination to keep it audible.
function tapAudioForRecording(el) {
  const dest = sessionRec.mixDest;
  if (!dest || !el) return;
  try {
    const ctx = getAudioCtx();
    const src = ctx.createMediaElementSource(el);
    src.connect(ctx.destination);
    src.connect(dest);
  } catch (e) {
    // Never let recording plumbing break playback (double-tap on the same
    // element throws, as does a cross-origin URL).
    logEvent('rec-tap-FAIL', { err: e.message });
  }
}

// Convert + send the finished recording to the WordPress endpoint.
// Fired from the "Send to teacher" button on the done panel.
async function sendSessionRecording(score) {
  if (!sessionRec.blob || !WP_LINK.token) return;
  if (sessionRec.uploadStatus === 'converting' || sessionRec.uploadStatus === 'sending') return;
  try {
    sessionRec.uploadStatus = 'converting';
    sessionRec.uploadError = '';
    logEvent('assignment-upload-start', { bytes: sessionRec.blob.size, mime: sessionRec.blob.type, score });
    const mp3 = sessionRec.blob.type.includes('mp3')
      ? sessionRec.blob
      : await blobToMp3(sessionRec.blob);
    sessionRec.uploadStatus = 'sending';
    const j = await uploadAssignmentRecording(mp3, score);
    sessionRec.uploadStatus = 'sent';
    sessionRec.assignmentId = String((j && j.assignment_id) || '');
    logEvent('assignment-upload-ok', { assignmentId: sessionRec.assignmentId, mp3Bytes: mp3.size });
  } catch (e) {
    sessionRec.uploadStatus = 'failed';
    sessionRec.uploadError = e.message;
    logEvent('assignment-upload-FAIL', { err: e.message });
  }
}

async function startSessionRecording() {
  if (sessionRec.status === 'recording') return;
  try {
    if (typeof MediaRecorder === 'undefined') throw new Error('Recording not supported on this browser');
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Mix mic + app audio (2026-08-12): record a MediaStreamDestination that
    // the mic feeds directly and every frog line gets tapped into (see
    // tapAudioForRecording). Mic does NOT connect to ctx.destination — no echo.
    // If the mix graph can't be built, fall back to mic-only rather than fail.
    let recStream = stream;
    try {
      const ctx = getAudioCtx();
      ctx.resume().catch(() => {});
      const dest = ctx.createMediaStreamDestination();
      ctx.createMediaStreamSource(stream).connect(dest);
      sessionRec.mixDest = dest;
      recStream = dest.stream;
    } catch (e) {
      sessionRec.mixDest = null;
      logEvent('session-record-mix-FAIL', { err: e.message });
    }
    // Safari records audio/mp4; everyone else webm/opus. Probe in order.
    const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
      .find((m) => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) || '';
    const mr = new MediaRecorder(recStream, mime ? { mimeType: mime } : undefined);
    if (sessionRec.url) { URL.revokeObjectURL(sessionRec.url); sessionRec.url = ''; }
    sessionRec.chunks = [];
    sessionRec.blob = null;
    mr.ondataavailable = (e) => { if (e.data && e.data.size) sessionRec.chunks.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(sessionRec.chunks, { type: mr.mimeType || mime || 'audio/webm' });
      sessionRec.blob = blob;
      sessionRec.url = URL.createObjectURL(blob);
      sessionRec.status = 'done';
      sessionRec.mixDest = null; // future frog lines stop feeding the mix
      stream.getTracks().forEach((t) => t.stop());
      logEvent('session-record-stop', {
        secs: Math.round((Date.now() - sessionRec.startedAt) / 1000),
        bytes: blob.size, mime: blob.type,
      });
    };
    mr.start(1000); // chunk every second so a crash loses at most 1s
    sessionRec.mediaRecorder = mr;
    sessionRec.stream = stream;
    sessionRec.startedAt = Date.now();
    sessionRec.error = '';
    sessionRec.status = 'recording';
    logEvent('session-record-start', { mime: mime || '(default)' });
  } catch (e) {
    sessionRec.status = 'error';
    sessionRec.error = (e.name === 'NotAllowedError' || e.name === 'NotFoundError')
      ? 'Microphone permission is needed to record.' : e.message;
    logEvent('session-record-FAIL', { err: `${e.name || ''} ${e.message}` });
  }
}

function stopSessionRecording() {
  if (sessionRec.mediaRecorder && sessionRec.status === 'recording') {
    try { sessionRec.mediaRecorder.stop(); } catch { /* already stopped */ }
  }
}

function dismissSessionRecording() {
  if (sessionRec.url) URL.revokeObjectURL(sessionRec.url);
  sessionRec.url = '';
  sessionRec.blob = null;
  sessionRec.chunks = [];
  sessionRec.status = 'idle';
  sessionRec.error = '';
  sessionRec.mixDest = null;
  sessionRec.uploadStatus = 'idle';
  sessionRec.uploadError = '';
  sessionRec.assignmentId = '';
}

// ─── AUTO-RECORD (2026-10-20, Natalya tracker #1) ───
// Young children won't see or know to tap a record button, so when the app is
// opened from a WordPress lesson link (the only place a recording can be
// submitted) the session records itself: starts on "Tap to Start", stops when
// the closing line finishes, and sends to the teacher with no button press.
// ✕ mid-session also stops + sends what was recorded before leaving, so every
// attempt reaches the teacher. Standalone/demo use keeps the manual button.
const AUTO_RECORD = !!WP_LINK.token;

function startAutoRecording() {
  if (!AUTO_RECORD) return;
  if (sessionRec.status === 'recording') return;
  if (sessionRec.status !== 'idle') dismissSessionRecording(); // fresh take for "play again"
  logEvent('auto-record-start', {});
  startSessionRecording();
}

// Stop the recorder, wait for the final blob, then send it. Resolves when the
// upload settles (sent or failed) or after timeoutMs — never throws.
async function finishAutoRecording(score, reason, timeoutMs = 20000) {
  if (!AUTO_RECORD || sessionRec.status !== 'recording') return;
  logEvent('auto-record-finish', { reason, score });
  stopSessionRecording();
  const t0 = Date.now();
  while (sessionRec.status === 'recording' && Date.now() - t0 < 3000) {
    await new Promise((r) => setTimeout(r, 100));
  }
  if (sessionRec.status !== 'done') return;
  await Promise.race([
    sendSessionRecording(score),
    new Promise((r) => setTimeout(r, Math.max(1000, timeoutMs - (Date.now() - t0)))),
  ]);
}

// Filename extension that matches what the browser actually recorded.
function sessionRecExt() {
  const t = (sessionRec.blob && sessionRec.blob.type) || '';
  return t.includes('mp4') ? 'm4a' : t.includes('ogg') ? 'ogg' : 'webm';
}

function SessionRecorderButton({ score = 0 }) {
  // Poll shared state, same technique as TimingOverlay.
  const [, force] = useState(0);
  useEffect(() => { const id = setInterval(() => force((n) => n + 1), 500); return () => clearInterval(id); }, []);
  const s = sessionRec;
  const base = { position: 'fixed', bottom: 14, right: 14, zIndex: 40, fontFamily: "'Nunito', sans-serif" };

  if (AUTO_RECORD && s.exitSaving) {
    return (
      <div onClick={(e) => e.stopPropagation()}
        style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(5,84,75,0.88)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: "'Nunito', sans-serif", color: BRAND.white, fontSize: 34, fontWeight: 900 }}>
        Saving your work…
      </div>
    );
  }
  if (AUTO_RECORD) {
    // Auto mode: nothing for a child to tap — a quiet status pill only.
    const pill = (bg, border, text) => (
      <div onClick={(e) => e.stopPropagation()}
        style={{ ...base, display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px',
          borderRadius: 999, border: `2px solid ${border}`, background: bg, color: BRAND.white,
          fontSize: 13, fontWeight: 800, boxShadow: '0 3px 10px rgba(0,0,0,0.2)', pointerEvents: 'none' }}>
        {text}
      </div>
    );
    if (s.status === 'recording') {
      return pill(BRAND.red, BRAND.redDark, (<>
        <style>{'@keyframes lsRecPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }'}</style>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: BRAND.white, animation: 'lsRecPulse 1.1s infinite' }} />
        Recording
      </>));
    }
    if (s.status === 'done') {
      if (s.uploadStatus === 'sent') return pill('#27AE60', '#1E8449', '✓ Sent to your teacher!');
      if (s.uploadStatus === 'failed') {
        return (
          <button onClick={(e) => { e.stopPropagation(); sendSessionRecording(score); }}
            style={{ ...base, padding: '8px 16px', borderRadius: 999, border: `2px solid ${BRAND.goldLight}`,
              background: BRAND.gold, color: BRAND.white, fontSize: 13, fontWeight: 800, cursor: 'pointer',
              fontFamily: "'Nunito', sans-serif", boxShadow: '0 3px 10px rgba(0,0,0,0.2)' }}>
            Couldn't send — tap to try again
          </button>
        );
      }
      if (s.uploadStatus === 'converting' || s.uploadStatus === 'sending') return pill(BRAND.teal, BRAND.tealLight, 'Sending to your teacher…');
      return null;
    }
    if (s.status === 'error') return pill(BRAND.gray, BRAND.gray, '🎙 Recording unavailable');
    return null; // idle — starts with Tap to Start
  }

  if (s.status === 'recording') {
    const secs = Math.floor((Date.now() - s.startedAt) / 1000);
    const clock = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
    return (
      <button onClick={(e) => { e.stopPropagation(); stopSessionRecording(); }}
        style={{ ...base, display: 'flex', alignItems: 'center', gap: 9, padding: '10px 18px',
          borderRadius: 999, border: `3px solid ${BRAND.redDark}`, background: BRAND.red,
          color: BRAND.white, fontSize: 15, fontWeight: 800, cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(0,0,0,0.3)' }}>
        <style>{'@keyframes lsRecPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }'}</style>
        <span style={{ width: 11, height: 11, borderRadius: '50%', background: BRAND.white,
          animation: 'lsRecPulse 1.1s infinite' }} />
        Recording {clock} — tap to stop
      </button>
    );
  }

  if (s.status === 'done') {
    return (
      <div onClick={(e) => e.stopPropagation()}
        style={{ ...base, width: 300, background: BRAND.white, border: `3px solid ${BRAND.teal}`,
        borderRadius: 16, padding: 12, boxShadow: '0 6px 20px rgba(0,0,0,0.28)' }}>
        <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 7, color: BRAND.teal }}>
          🎧 Your session recording
        </div>
        <audio controls src={s.url} style={{ width: '100%', height: 36 }} />
        {/* Send-to-teacher: only when opened from a WordPress-generated link */}
        {WP_LINK.token && (
          s.uploadStatus === 'sent' ? (
            <div style={{ marginTop: 9, padding: '8px 0', borderRadius: 10, background: '#E8F8EF',
              border: '2px solid #27AE60', color: '#1E8449', fontWeight: 800, fontSize: 13, textAlign: 'center' }}>
              ✓ Sent to your teacher!
            </div>
          ) : (
            <>
              <button onClick={(e) => { e.stopPropagation(); sendSessionRecording(score); }}
                disabled={s.uploadStatus === 'converting' || s.uploadStatus === 'sending'}
                style={{ width: '100%', marginTop: 9, padding: '9px 0', borderRadius: 10,
                  border: `2px solid ${BRAND.goldLight}`, background: BRAND.gold, color: BRAND.white,
                  fontWeight: 800, fontSize: 13, fontFamily: "'Nunito', sans-serif",
                  cursor: (s.uploadStatus === 'converting' || s.uploadStatus === 'sending') ? 'default' : 'pointer',
                  opacity: (s.uploadStatus === 'converting' || s.uploadStatus === 'sending') ? 0.7 : 1 }}>
                {s.uploadStatus === 'converting' ? 'Preparing…'
                  : s.uploadStatus === 'sending' ? 'Sending…'
                  : s.uploadStatus === 'failed' ? 'Try again'
                  : '📤 Send to teacher'}
              </button>
              {s.uploadStatus === 'failed' && (
                <div style={{ marginTop: 5, fontSize: 11, fontWeight: 700, color: BRAND.redDark }}>
                  Couldn't send: {s.uploadError}
                </div>
              )}
            </>
          )
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
          <a href={s.url} download={`session-${SESSION_ID}.${sessionRecExt()}`}
            style={{ flex: 1, textAlign: 'center', padding: '8px 0', borderRadius: 10,
              background: BRAND.teal, color: BRAND.white, fontWeight: 800, fontSize: 13,
              textDecoration: 'none' }}>
            Download
          </a>
          <button onClick={(e) => { e.stopPropagation(); dismissSessionRecording(); }}
            style={{ flex: 1, padding: '8px 0', borderRadius: 10, border: `2px solid ${BRAND.gray}`,
              background: BRAND.white, color: BRAND.gray, fontWeight: 800, fontSize: 13,
              cursor: 'pointer' }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  if (s.status === 'error') {
    return (
      <div onClick={(e) => e.stopPropagation()}
        style={{ ...base, maxWidth: 280, background: BRAND.cream, border: `3px solid ${BRAND.red}`,
        borderRadius: 14, padding: '10px 12px', boxShadow: '0 4px 14px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.redDark, marginBottom: 7 }}>{s.error}</div>
        <button onClick={(e) => { e.stopPropagation(); dismissSessionRecording(); }}
          style={{ padding: '6px 14px', borderRadius: 10, border: `2px solid ${BRAND.gray}`,
            background: BRAND.white, color: BRAND.gray, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
          OK
        </button>
      </div>
    );
  }

  // idle
  return (
    <button onClick={(e) => { e.stopPropagation(); startSessionRecording(); }}
      style={{ ...base, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px',
        borderRadius: 999, border: `3px solid ${BRAND.tealLight}`, background: BRAND.teal,
        color: BRAND.white, fontSize: 15, fontWeight: 800, cursor: 'pointer',
        boxShadow: '0 4px 14px rgba(0,0,0,0.25)' }}>
      🎙 Record your session
    </button>
  );
}

// ════════════════════════════════════════════════════════
// ─── LOCAL FUZZY WORD MATCHING
// ════════════════════════════════════════════════════════
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

// Some speech engines return punctuation/odd casing ("Horses." / "Four!").
// Strip everything but letters, digits, spaces and apostrophes before matching.
// Spanish letters (áéíóúüñ) are kept (2026-08-12, Spanish track) — harmless
// for English, essential for targets like "marrón".
const cleanSpoken = (s) => s.toLowerCase().replace(/[^a-z0-9'áéíóúüñ ]/g, ' ').replace(/\s+/g, ' ').trim();

function fuzzyMatch(spokenAlternatives, targetWord) {
  const target = targetWord.toLowerCase().trim();
  console.log(`[MATCH] target="${target}", alternatives=`, spokenAlternatives);

  for (const alt of spokenAlternatives) {
    const spoken = cleanSpoken(alt);
    if (spoken === target) return true;
    if (spoken.includes(target)) return true;
    const words = spoken.split(/\s+/);
    for (const w of words) {
      if (w === target) return true;
      if (w.length >= target.length - 1 && levenshtein(w, target) <= 1) return true;
    }
    if (spoken.endsWith('s') && levenshtein(spoken.slice(0, -1), target) <= 1) return true;
  }
  return false;
}

// Also check if spoken matches ANY of several valid answers
function fuzzyMatchAny(spokenAlternatives, targetWords) {
  return targetWords.some((t) => fuzzyMatch(spokenAlternatives, t));
}

// ─── STT sound-alike rescues (2026-07-12, Mark's "ducks"→"dogs" test) ───
// Words the recognizer confuses with a target. Applied ONLY where the target
// is KNOWN (card-game tiers) — never in open-ended matching: on the favorite
// question "dog" is a real wrong answer, but a child looking at a duck card
// whose "ducks" transcribes as "dogs" is right. Levenshtein can't rescue
// these (distance 2), hence the explicit map. Keep it to OBSERVED pairs.
const STT_CONFUSIONS = {
  duck: ['dog', 'dogs', 'dock', 'docks', 'duct', 'ducts'],
};

// Valid answers for a card's name tier: word + plural + known STT confusions
// + the card's own accepted variants (the script's "also counts as correct"
// column — e.g. mommy/mother for mom, skipping rope for jump rope).
function nameTierWords(animal) {
  return [animal.word, animal.plural,
    ...(STT_CONFUSIONS[animal.word] || []),
    ...(animal.extraWords || [])];
}

// ─── Yes/No detection (School 'yesno' + 'feelings' tiers, 2026-08-17) ───
// Fully local — yes and no are BOTH correct answers (the star is for
// answering), so no judge call is ever needed. Word-order matters: the first
// polarity word wins, so "no, I don't" is a no even though "do" appears later.
const YES_WORDS = new Set(['yes', 'yeah', 'yep', 'yup', 'ya', 'yay', 'sure', 'okay', 'ok']);
const NO_WORDS = new Set(['no', 'nope', 'nah']);
function detectYesNo(spokenAlternatives) {
  for (const alt of spokenAlternatives) {
    const words = cleanSpoken(alt).split(/\s+/).filter(Boolean);
    for (const w of words) {
      if (NO_WORDS.has(w)) return 'no';
      if (YES_WORDS.has(w)) return 'yes';
    }
    const joined = words.join(' ');
    if (/\b(?:don't|dont|do not|not)\b/.test(joined)) return 'no';   // "I do not have a pencil"
    if (/\bi (?:do|have|like|love)\b/.test(joined) || /\b(?:have|like|love)\b/.test(joined)) return 'yes'; // "I have a pencil" / "I like fruits" / "I love fruits"
  }
  return null;
}

// Did the child produce the full sentence (vs a bare yes/no)? The verbs are
// the pack's ladder verbs — School: "Yes, I have a pencil." (default 'have');
// Garden: "Yes, I like vegetables." + 'love' as a natural synonym ("I love
// fruits, they're so sweet" — Polina's play test, 2026-08-18).
const YESNO_VERBS = ADVENTURE.yesNoVerbs || [ADVENTURE.yesNoVerb || 'have'];
function saidHaveSentence(spokenAlternatives) {
  return spokenAlternatives.some((alt) => {
    const words = cleanSpoken(alt).split(/\s+/).filter(Boolean);
    return YESNO_VERBS.some((v) => words.includes(v)) && words.length >= 3;
  });
}

// Number word matching (accept "1" or "one", etc.)
// STRICT — no fuzzy/levenshtein for numbers (single digits are all distance 1 from each other)
// A language pack may supply its own words (farm-es: uno/dos/tres...).
const NUMBER_WORDS = ADVENTURE.numberWords || {
  1: ['one', '1', 'won'], 2: ['two', '2', 'to', 'too', 'tu'], 3: ['three', '3'],
  4: ['four', '4', 'for'], 5: ['five', '5'], 6: ['six', '6'],
  7: ['seven', '7'], 8: ['eight', '8', 'ate'], 9: ['nine', '9'],
  10: ['ten', '10'],
};

function fuzzyMatchNumber(spokenAlternatives, targetNum) {
  const validWords = NUMBER_WORDS[targetNum] || [];
  // Strict exact match only — no levenshtein for numbers
  for (const alt of spokenAlternatives) {
    const words = cleanSpoken(alt).split(/\s+/);
    for (const w of words) {
      if (validWords.includes(w)) return true;
    }
  }
  return false;
}

// ─── Compound-answer bonus credit (2026-08-03, conference kid test) ───
// A child who answers "I see two pink pigs" on the NAME question has already
// answered the color and count questions too. Re-asking "What color are the
// pigs?" right after reads as deaf ("I just told you pink!"). So: any LATER
// tier of the current card that the answer already covers gets its star
// immediately and is skipped — full credit, faster round (Natalya + Polina,
// 2026-08-03).
// Crediting is STRICTER than answering the asked question: a volunteered
// count only matches the unambiguous forms ("two"/"2"), never the homophones
// ("to", "for", "ate") that show up in ordinary sentences — those stay
// reserved for the count question itself, where number context is expected.
const STRICT_NUMBER_WORDS = ADVENTURE.strictNumberWords || {
  1: ['one', '1'], 2: ['two', '2'], 3: ['three', '3'], 4: ['four', '4'],
  5: ['five', '5'], 6: ['six', '6'], 7: ['seven', '7'], 8: ['eight', '8'],
  9: ['nine', '9'], 10: ['ten', '10'],
};
function numberSaidStrict(spokenAlternatives, targetNum) {
  const valid = STRICT_NUMBER_WORDS[targetNum] || [];
  return spokenAlternatives.some((alt) =>
    cleanSpoken(alt).split(/\s+/).some((w) => valid.includes(w)));
}

// Which of the card's LATER tiers did this answer already cover?
// Respects each card's own tiers array (duck-multi never credits color, a
// name-only card credits nothing).
function volunteeredTiers(alternatives, animal, currentTier) {
  const tiers = animal.tiers || ['name', 'color', ...(animal.count > 1 ? ['count'] : [])];
  const remaining = tiers.slice(tiers.indexOf(currentTier) + 1);
  return remaining.filter((t) =>
    t === 'color'
      ? Boolean(animal.color) && animal.color.split(/\s+and\s+/).some((cw) => fuzzyMatch(alternatives, cw))
      : t === 'count'
        ? numberSaidStrict(alternatives, animal.count)
        : false);
}

// ════════════════════════════════════════════════════════
// ─── AUDIO SYSTEM
// ════════════════════════════════════════════════════════

// ─── Voice source switch ───
// 'gemini'     → WAVs in /audio-gemini/<character>/ — the frog's Puck library
//                (prewarm-audio.mjs) or Mishka's Spanish library
//                (tts/gen-farm-es-audio.mjs). Picked by CHARACTER below.
// 'elevenlabs' → legacy mp3s in /audio/ (pre-Gemini library, kept for A/B)
// One-word change to swap the whole voice library at runtime.
const VOICE_SOURCE = 'gemini';

// Playback speed for the frog's voice. The pre-rendered WAVs felt a touch slow,
// so nudge the tempo up. preservesPitch keeps the same voice (no chipmunk).
// Tune here — 1.0 = original, 1.1 = ~10% faster.
const SPEECH_RATE = 1.1;

const resolveAudioPath = (p) =>
  VOICE_SOURCE === 'gemini'
    ? p.replace(/^\/audio\//, `/audio-gemini/${CHARACTER.id}/`).replace(/\.mp3$/, '.wav')
    : p;

// Decode the base64 audio returned by /api/speak into a playable Blob.
function base64ToBlob(b64, mime) {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

// Play an audio URL through the shared (gesture-unlocked) AudioContext.
// Used when <audio>.play() is blocked by iOS autoplay policy. Plays at 1.0×
// (WebAudio playbackRate would shift pitch, unlike preservesPitch).
async function playViaWebAudio(url, setSpeaking) {
  const ctx = getAudioCtx();
  try { await ctx.resume(); } catch { /* ignore */ }
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${res.status}`);
  const buf = await ctx.decodeAudioData(await res.arrayBuffer());
  await new Promise((resolve) => {
    // Safety net: if iOS keeps the context suspended, onended never fires —
    // resolve after the clip's duration + grace so the game can't hang mute.
    const failsafe = setTimeout(resolve, Math.ceil(buf.duration * 1000) + 1500);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    // iOS fallback path plays through WebAudio directly — feed the session
    // recording mix here too, or recorded sessions on iOS lose the frog.
    if (sessionRec.mixDest) { try { src.connect(sessionRec.mixDest); } catch { /* ignore */ } }
    src.onended = () => { clearTimeout(failsafe); resolve(); };
    if (setSpeaking) setSpeaking(true);
    src.start(0);
  });
}

function useAudioPlayer() {
  const audioRef = useRef(null);
  const [speaking, setSpeaking] = useState(false);

  const speak = useCallback(async (audioPath, fallbackText) => {
    try {
      // iOS: each mic-capture turn flips the audio session between record and
      // playback; the shared AudioContext can come out of it 'suspended' or
      // 'interrupted' and playback goes silent for a few seconds (Polina's
      // iPhone horses card, 2026-07-02). Nudge it awake before every line and
      // leave a trace in the log when it wasn't running.
      try {
        const ctx = getAudioCtx();
        if (ctx.state !== 'running') {
          logEvent('audio-ctx-not-running', { state: ctx.state, path: audioPath });
          ctx.resume().catch(() => {});
        }
      } catch { /* never block speech on this */ }
      if (audioRef.current) { audioRef.current.pause(); }

      const audio = new Audio(resolveAudioPath(audioPath));
      audio.preload = 'auto';
      audio.preservesPitch = true; // hold the frog's pitch while we speed it up
      audio.playbackRate = SPEECH_RATE;
      audioRef.current = audio;
      tapAudioForRecording(audio); // frog's line into the session recording

      // Set up the 'playing' listener so the avatar animation flips on at the
      // EXACT moment audio starts producing sound. This avoids both the
      // "animation runs ahead of audio" glitch AND the previous canplay-gate
      // bug where some files were silently skipped if canplay didn't fire fast
      // enough. The 'playing' event is the most reliable sync signal.
      audio.addEventListener('playing', () => setSpeaking(true), { once: true });

      // ended/error listeners drive the playback completion promise.
      const finished = new Promise((resolve) => {
        audio.addEventListener('ended', () => resolve('done'), { once: true });
        audio.addEventListener('error', () => resolve('error'), { once: true });
      });

      // audio.play() returns a Promise that resolves when playback has
      // actually started (or rejects if blocked: missing file, decode error,
      // autoplay policy, etc.). The browser handles loading internally.
      try {
        await audio.play();
      } catch (err) {
        // iOS/Safari autoplay policy: <audio>.play() outside a user gesture can
        // be blocked (NotAllowedError). The shared AudioContext was unlocked on
        // the Tap-to-Start gesture, so route the same file through WebAudio.
        if (err.name === 'NotAllowedError') {
          logEvent('audio-webaudio-fallback', { path: audioPath });
          try {
            await playViaWebAudio(resolveAudioPath(audioPath), setSpeaking);
            return;
          } catch (e2) {
            logEvent('audio-FAIL', { path: audioPath, err: `webaudio: ${e2.message}` });
            return;
          }
        }
        logEvent('audio-FAIL', { path: audioPath, err: err.message });
        console.warn(`[audio] play failed for ${audioPath}:`, err.message);
        return; // caller's .then() still fires; game advances; no ghost animation
      }

      const playResult = await finished;

      if (playResult === 'error' && fallbackText) {
        console.log(`Fallback TTS for: ${audioPath}`);
        try {
          const res = await fetch(`${API_BASE}/api/speak`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: fallbackText, character: CHARACTER.id }),
          });
          if (res.ok) {
            const { audio, mime } = await res.json();
            const blob = base64ToBlob(audio, mime || 'audio/wav');
            const url = URL.createObjectURL(blob);
            const fallback = new Audio(url);
            fallback.preservesPitch = true;
            fallback.playbackRate = SPEECH_RATE;
            audioRef.current = fallback;
            tapAudioForRecording(fallback);
            await new Promise((resolve) => {
              fallback.onended = resolve;
              fallback.onerror = resolve;
              fallback.play().catch(resolve);
            });
          }
        } catch { /* silent */ }
      }
    } catch (err) {
      console.error('Audio error:', err);
    } finally {
      setSpeaking(false);
    }
  }, []);

  // Speak arbitrary text with the dynamic Gemini Puck voice (AI mode). Same voice
  // and delivery as the pre-baked library, just generated on the fly.
  const speakText = useCallback(async (text) => {
    if (!text) return;
    try {
      setSpeaking(true);
      const ttsStart = performance.now();
      const res = await fetch(`${API_BASE}/api/speak`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, character: CHARACTER.id }),
      });
      if (!res.ok) throw new Error('speak failed');
      const { audio, mime } = await res.json();
      const url = URL.createObjectURL(base64ToBlob(audio, mime || 'audio/wav'));
      const a = new Audio(url);
      a.preservesPitch = true; a.playbackRate = SPEECH_RATE;
      audioRef.current = a;
      tapAudioForRecording(a);
      await new Promise((resolve) => {
        a.addEventListener('playing', () => {
          setSpeaking(true);
          if (!aiTimings.tts) {
            aiTimings.tts = Math.round(performance.now() - ttsStart);
            aiTimings.total = Math.round(performance.now() - aiTimings.turnStart);
            logEvent('ai-timing', { phase: 'speak', tts: aiTimings.tts, total: aiTimings.total });
          }
        }, { once: true });
        a.onended = () => { URL.revokeObjectURL(url); resolve(); };
        a.onerror = () => { URL.revokeObjectURL(url); resolve(); };
        a.play().catch(resolve);
      });
    } catch (e) {
      logEvent('speakText-FAIL', e.message);
    } finally {
      setSpeaking(false);
    }
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setSpeaking(false);
  }, []);

  return { speak, speakText, stop, speaking };
}

// ─── Hook: Web Speech API STT ───
function useSTT() {
  const recogRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [result, setResult] = useState({ text: '', alternatives: [], id: 0 });
  const idRef = useRef(0);
  const timeoutRef = useRef(null);

  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    // No Web Speech here (Safari, Firefox, most mobile). This path is only
    // reachable via ?stt=browser — the default universal STT doesn't need SR.
    if (!SR) { logEvent('mic-unsupported', 'Web Speech unavailable; use default ?stt=live mode'); return; }
    logEvent('mic-start');

    if (recogRef.current) { try { recogRef.current.abort(); } catch (_) {} }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); }

    const recog = new SR();
    recog.lang = ADVENTURE.sttLang || 'en-US';
    recog.interimResults = false;
    recog.maxAlternatives = 5;
    recog.continuous = false;

    let gotResult = false; // so onend can report whether the recognizer caught anything
    const startedAt = Date.now();
    recog.onresult = (e) => {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      gotResult = true;
      const alts = [];
      for (let i = 0; i < e.results[0].length; i++) {
        alts.push(e.results[0][i].transcript.toLowerCase().trim());
      }
      const best = alts[0] || '';
      logEvent('mic-result', { heard: best, alts });
      idRef.current += 1;
      setResult({ text: best, alternatives: alts, id: idRef.current });
      setListening(false);
    };
    recog.onerror = (e) => {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      // e.error names the cause: 'not-allowed' = mic permission denied,
      // 'audio-capture' = no usable microphone, 'no-speech' = heard nothing.
      logEvent('mic-ERROR', e.error || 'unknown');
      console.warn('Speech recognition error:', e.error, e.message);
      setListening(false);
    };
    recog.onend = () => {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      // The silent failure the kid feels as "it didn't hear me": recognizer
      // started, ended, but caught nothing AND raised no error. Log how long it
      // ran — a near-instant end points to mic/audio-capture not engaging.
      if (!gotResult) logEvent('mic-end-empty', { ranMs: Date.now() - startedAt });
      setListening(false);
    };

    recogRef.current = recog;
    setListening(true);
    try { recog.start(); } catch (e) {
      logEvent('mic-start-FAILED', e.message || String(e));
      console.error('Speech recognition start failed:', e);
      setListening(false);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      logEvent('mic-timeout', '8s with no result');
      try { recog.abort(); } catch (_) {}
      setListening(false);
      timeoutRef.current = null;
    }, 8000);
  }, []);

  const stopListening = useCallback(() => {
    if (recogRef.current) { try { recogRef.current.stop(); } catch (_) {} }
    setListening(false);
  }, []);

  return { startListening, stopListening, listening, transcript: result.text, alternatives: result.alternatives, transcriptId: result.id };
}

// ─── base64 + recorder helpers for Gemini listening ───
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
function pickRecMime() {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  for (const t of types) { if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) return t; }
  return 'audio/webm';
}

// ─── Hook: hybrid STT — browser Web Speech (instant) + Gemini fallback ───
// The browser recognizer is fast but sometimes hears nothing on kids' voices.
// So we ALSO record audio in parallel: if the browser returns a result we use it
// immediately; only if it comes back empty do we send the recording to Gemini
// (record-the-whole-clip-then-send — no streaming needed). Fast in the common
// case, Gemini rescues the misses. Same return shape as useSTT.
function useHybridSTT() {
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [result, setResult] = useState({ text: '', alternatives: [], id: 0 });
  const idRef = useRef(0);
  const recogRef = useRef(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const acRef = useRef(null);
  const doneRef = useRef(false);
  const recStartRef = useRef(0);

  // First result wins (browser or Gemini); the other is ignored.
  const deliver = useCallback((text, alts) => {
    if (doneRef.current) return;
    doneRef.current = true;
    idRef.current += 1;
    setResult({ text, alternatives: alts, id: idRef.current });
  }, []);

  const stopRecorder = () => {
    try { if (mrRef.current && mrRef.current.state === 'recording') mrRef.current.stop(); } catch (_) {}
  };

  const startListening = useCallback(async () => {
    resetTimings();
    doneRef.current = false;
    chunksRef.current = [];
    setListening(true);

    // 1) Record audio in parallel — the Gemini fallback source.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      const mr = new MediaRecorder(stream, { mimeType: pickRecMime() });
      mrRef.current = mr;
      recStartRef.current = performance.now();
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        aiTimings.record = Math.round(performance.now() - recStartRef.current);
        stream.getTracks().forEach((t) => t.stop());
        if (acRef.current) { try { acRef.current.close(); } catch (_) {} acRef.current = null; }
        setListening(false);
        if (doneRef.current) { logEvent('hstt-browser-won', { record: aiTimings.record }); return; } // browser already delivered
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        if (!blob.size) { deliver('', []); return; }
        setTranscribing(true);
        const sttStart = performance.now();
        try {
          const audioBase64 = await blobToBase64(blob);
          const res = await fetch(`${API_BASE}/api/listen`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ audioBase64, mimeType: mr.mimeType }),
          });
          const data = await res.json();
          const alts = (data.alternatives || []).map((s) => String(s).toLowerCase().trim()).filter(Boolean);
          const best = (data.transcript || alts[0] || '').toLowerCase().trim();
          aiTimings.stt = Math.round(performance.now() - sttStart);
          aiTimings.total = Math.round(performance.now() - aiTimings.turnStart);
          logEvent('ai-timing', { phase: 'listen-gemini', record: aiTimings.record, stt: aiTimings.stt, heard: best });
          deliver(best, alts.length ? alts : (best ? [best] : []));
        } catch (e) { logEvent('hstt-gemini-ERROR', e.message); deliver('', []); }
        finally { setTranscribing(false); }
      };
      mr.start();
      // Stop ~1.1s after the child stops talking (hard cap 6s).
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ac = new AC(); acRef.current = ac;
        const an = ac.createAnalyser(); an.fftSize = 512;
        ac.createMediaStreamSource(stream).connect(an);
        const buf = new Uint8Array(an.fftSize);
        let heard = false; let lastLoud = performance.now(); const t0 = performance.now();
        const tick = () => {
          if (!mrRef.current || mrRef.current.state !== 'recording') return;
          an.getByteTimeDomainData(buf);
          let sum = 0; for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
          const rms = Math.sqrt(sum / buf.length); const now = performance.now();
          if (rms > 0.06) { heard = true; lastLoud = now; }
          if ((heard && now - lastLoud > 1100 && now - t0 > 500) || now - t0 > 6000) { stopRecorder(); return; }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      } catch (_) {
        setTimeout(stopRecorder, 3000);
      }
    } catch (e) {
      logEvent('hstt-mic-FAIL', e.message); setListening(false);
    }

    // 2) Browser Web Speech in parallel — the fast primary.
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const recog = new SR();
      recog.lang = ADVENTURE.sttLang || 'en-US'; recog.interimResults = false; recog.maxAlternatives = 5; recog.continuous = false;
      recog.onresult = (e) => {
        const alts = []; for (let i = 0; i < e.results[0].length; i++) alts.push(e.results[0][i].transcript.toLowerCase().trim());
        const best = alts[0] || '';
        if (!best) return; // empty → let the recorder's Gemini fallback handle it
        aiTimings.stt = 0;
        aiTimings.total = Math.round(performance.now() - aiTimings.turnStart);
        logEvent('ai-timing', { phase: 'listen-browser', record: aiTimings.record, heard: best });
        deliver(best, alts);
        stopRecorder(); // we don't need Gemini; onstop sees doneRef and skips it
      };
      recog.onerror = (e) => { logEvent('hstt-browser-err', e.error || '?'); };
      recogRef.current = recog;
      try { recog.start(); } catch (e) { logEvent('hstt-browser-start-FAIL', e.message); }
    }
  }, [deliver]);

  const stopListening = useCallback(() => {
    try { if (recogRef.current) recogRef.current.stop(); } catch (_) {}
    stopRecorder();
  }, []);

  return { startListening, stopListening, listening, transcribing, transcript: result.text, alternatives: result.alternatives, transcriptId: result.id };
}

// ════════════════════════════════════════════════════════
// ─── UNIVERSAL STT — streaming Gemini Live, works on any browser
// ════════════════════════════════════════════════════════
// One mic pipeline (WebAudio PCM capture — no Web Speech, no MediaRecorder)
// feeding three consumers:
//   1. STREAMING: chunks go to Gemini Live over the browser's own WebSocket
//      while the child speaks → transcript is ready ~when they stop.
//   2. FALLBACK: the same PCM, WAV-wrapped, goes to turn-based /api/listen if
//      streaming isn't available (token/socket failure, corporate firewall).
//   3. RECORDING: the same WAV is uploaded to the private diag bucket so every
//      answer's real audio sits next to the session log (Natalya's request).
// Local RMS silence detection (~1.5s after speech, 8s hard cap) decides when
// the turn ends. Raised from 1.1s/6s on 2026-07-16 so kids speaking sentences
// aren't clipped mid-utterance (see the watchdog note below).

let liveSession = null;
function getLiveSession() {
  if (!liveSession) liveSession = new LiveSTT({ apiBase: API_BASE, logEvent });
  return liveSession;
}

// What KIND of answer the next turn expects ("a number from one to ten").
// Set by the game whenever the question changes; sent to /api/listen as a
// class-level hint. This is the fix for the "two" problem (2026-07-02):
// isolated short words transcribe as noise without context.
let STT_CONTEXT = '';
function setSttContext(c) { STT_CONTEXT = c || ''; }

// ASR engines emit placeholder tokens for unclear audio — "<noise>",
// "[inaudible]", "(unintelligible)". Those are NOT transcripts; treat them as
// silence so the fallback engine gets its shot at the same audio.
const JUNK_TOKEN_RE = /<[^>]*>|\[[^\]]*\]|\([^)]*\)/g;
function sanitizeHeard(s) {
  const cleaned = String(s || '').replace(JUNK_TOKEN_RE, ' ').replace(/\s+/g, ' ').trim();
  // Punctuation-only "transcripts" are placeholders too — the live engine
  // returned a bare "-" on Polina's iPhone geese-sentence turns (2026-07-03)
  // and it slipped past the bracket filter, blocking the fallback engine.
  // No letters or digits in ANY script = not speech.
  return /[\p{L}\p{N}]/u.test(cleaned) ? cleaned : '';
}

// Monotonic turn number for the whole session — stamped on every turn-scoped
// log event AND the uploaded audio filename, so log lines ↔ audio files link up.
let TURN_N = 0;

// Upload one turn's audio to the diag collector (fire-and-forget; the game
// never waits on this). Kind:'audio' routes it to .../<session>/audio/ in S3.
function uploadTurnAudio(pcmChunks, turn, meta = {}) {
  if (!DIAG_ENDPOINT) return;
  try {
    const wav = encodeWav(pcmChunks);
    if (wav.length <= 44) return; // header-only = nothing captured
    const payload = JSON.stringify({
      kind: 'audio', session: SESSION_ID, tester: TESTER, turn,
      tier: meta.tier || '', card: meta.card || '',
      wav: bytesToB64(wav),
    });
    // NO keepalive here: browsers cap keepalive bodies at 64KB, which silently
    // killed every recording longer than ~1.5s ("Load failed", found in
    // Polina's 2026-07-02 iPhone session — only a 43KB clip survived). Uploads
    // happen mid-game, not at page close, so keepalive buys nothing anyway.
    fetch(DIAG_ENDPOINT, { method: 'POST', body: payload })
      .then((r) => r.json().catch(() => null))
      .then((j) => logEvent('turn-audio', { turn, bytes: wav.length, key: (j && j.key) || '' }))
      .catch((e) => logEvent('turn-audio-FAIL', { turn, err: e.message }));
  } catch (e) {
    logEvent('turn-audio-FAIL', { turn, err: e.message });
  }
}

function useUniversalSTT() {
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [result, setResult] = useState({ text: '', alternatives: [], id: 0, turn: 0, source: '' });
  // Words heard so far THIS turn (live engine only) — feeds the "You said"
  // strip while the child is still talking (tracker #12 follow-up).
  const [partialText, setPartialText] = useState('');
  const idRef = useRef(0);
  const capRef = useRef(null);
  const doneRef = useRef(false);
  const finishRef = useRef(null);

  const deliver = useCallback((text, alts, source, turn) => {
    if (doneRef.current) return;
    doneRef.current = true;
    idRef.current += 1;
    const clean = (text || '').toLowerCase().trim();
    const cleanAlts = (alts || []).map((s) => String(s).toLowerCase().trim()).filter(Boolean);
    logEvent('stt-result', { turn, source, heard: clean, alts: cleanAlts });
    setResult({ text: clean, alternatives: cleanAlts.length ? cleanAlts : (clean ? [clean] : []), id: idRef.current, turn, source });
  }, []);

  const startListening = useCallback(async () => {
    if (capRef.current) return; // already listening
    resetTimings();
    doneRef.current = false;
    TURN_N += 1;
    const turn = TURN_N;
    setListening(true);
    setPartialText('');
    logEvent('mic-start', { turn, mode: STT_MODE });

    // Streaming session: connect in parallel with the mic opening. A failure
    // just means this turn silently uses the turn-based fallback.
    const wantLive = STT_MODE === 'live';
    const live = wantLive ? getLiveSession() : null;
    const livePromise = live
      ? live.ensureConnected().then(() => true).catch((e) => { logEvent('live-connect-FAIL', { turn, err: e.message }); return false; })
      : Promise.resolve(false);

    const t0 = performance.now();
    const pcmChunks = [];
    let heard = false;
    let lastLoud = t0;
    let liveUp = false;

    // End-of-turn: stop capture, resolve transcript (live → fallback), upload audio.
    const finishTurn = async () => {
      if (!capRef.current) return;
      const cap = capRef.current;
      capRef.current = null;
      finishRef.current = null;
      cap.stop();
      aiTimings.record = Math.round(performance.now() - t0);
      setListening(false);
      setTranscribing(true);
      const sttStart = performance.now();

      // RECORDING: ship the turn's audio regardless of what STT does with it.
      uploadTurnAudio(pcmChunks, turn);

      let text = '';
      let alts = [];
      let source = 'none';
      try {
        if (liveUp && live) {
          // sanitize: "<noise>" and friends are placeholders, not transcripts —
          // treating them as empty is what lets the fallback engine run.
          const rawLive = await live.endTurn(1500);
          text = sanitizeHeard(rawLive);
          if (text) source = 'live';
          else if (rawLive) logEvent('live-junk-token', { turn, raw: rawLive.slice(0, 60) });
        }
        // A bare digit ("4") hides which language the child spoke — cuatro and
        // four both digitize. The prompt asks the live engine for words, but
        // that's soft; when a digit slips through anyway (Polina's "cuatro"
        // repro, 2026-07-08), re-listen with the fallback engine, which
        // reliably returns number WORDS. Keep the digit as a backup.
        let digitBackup = '';
        if (source === 'live' && /^\d+[\s.!?]*$/.test(text)) {
          logEvent('live-digit-retry', { turn, digit: text });
          digitBackup = text;
          text = '';
          source = 'none';
        }
        if (!text && heard) {
          // Turn-based fallback: same PCM as WAV → /api/listen (server STT),
          // WITH the answer-class hint — its edge on short words like "two".
          try {
            const res = await fetch(`${API_BASE}/api/listen`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ audioBase64: bytesToB64(encodeWav(pcmChunks)), mimeType: 'audio/wav', context: STT_CONTEXT }),
            });
            const data = await res.json();
            alts = (data.alternatives || []).map(sanitizeHeard).filter(Boolean);
            text = sanitizeHeard(data.transcript) || alts[0] || '';
            if (text) source = 'turn-api';
          } catch (e) {
            logEvent('listen-api-FAIL', { turn, err: e.message });
          }
        }
        // Fallback came up empty but we had a digit — better than nothing.
        if (!text && digitBackup) { text = digitBackup; source = 'live'; }
      } finally {
        aiTimings.stt = Math.round(performance.now() - sttStart);
        aiTimings.total = Math.round(performance.now() - aiTimings.turnStart);
        logEvent('ai-timing', { phase: 'listen', turn, source, record: aiTimings.record, stt: aiTimings.stt, heard: text });
        if (!heard && !text) logEvent('turn-empty', { turn, ranMs: Math.round(performance.now() - t0) });
        if (live) live.onPartial = null;
        setTranscribing(false);
        setPartialText('');
        deliver(text, alts, source, turn);
      }
    };
    finishRef.current = finishTurn;

    // Open the mic.
    let cap;
    try {
      cap = await startCapture({
        onChunk: (int16) => {
          pcmChunks.push(int16);
          if (liveUp && live) live.sendAudioB64(int16ToB64(int16));
        },
        onLevel: (rms) => {
          if (rms > 0.06) { heard = true; lastLoud = performance.now(); }
        },
      });
    } catch (e) {
      logEvent('mic-FAIL', { turn, err: e.message });
      setListening(false);
      finishRef.current = null;
      deliver('', [], 'mic-fail', turn);
      return;
    }
    capRef.current = cap;

    const connected = await livePromise;
    // Guard: if the turn already ended while the socket was connecting (fast
    // speaker + slow connect), do NOT stream stale audio into the next turn.
    if (connected && live && capRef.current === cap) {
      live.beginTurn();
      live.onPartial = (t) => setPartialText(sanitizeHeard(t).toLowerCase());
      // Catch-up: chunks captured while the socket was still connecting.
      const already = pcmChunks.length;
      for (let i = 0; i < already; i++) live.sendAudioB64(int16ToB64(pcmChunks[i]));
      liveUp = true; // future chunks stream directly from onChunk
    }

    // Silence watchdog: end ~1.5s after the child stops talking, 8s hard cap.
    // Raised from 1.1s/6s (2026-07-16, Natalya "sentences cut off"): young kids
    // pause >1.1s mid-sentence ("these are ... two ... pink pigs"), so the old
    // window ended the turn early and clipped the tail. 1.5s tolerates the pause
    // while still feeling responsive; 8s cap gives longer sentences room.
    // NOTE: starting estimate — confirm against the next session's diag logs
    // (turn-summary/ai-timing events) and tune from real pause lengths.
    const tick = () => {
      if (!capRef.current) return;
      const now = performance.now();
      if ((heard && now - lastLoud > 1500 && now - t0 > 500) || now - t0 > 8000) { finishTurn(); return; }
      setTimeout(tick, 120); // setTimeout, not rAF — keeps ticking if the tab loses focus
    };
    tick();
  }, [deliver]);

  const stopListening = useCallback(() => {
    if (finishRef.current) finishRef.current();
  }, []);

  return {
    startListening, stopListening, listening, transcribing,
    transcript: result.text, alternatives: result.alternatives,
    transcriptId: result.id, turn: result.turn, source: result.source,
    partialText,
  };
}

// ─── Dev timing readout (AI mode) — last turn's latency breakdown ───
function TimingOverlay() {
  const [, force] = useState(0);
  useEffect(() => { const id = setInterval(() => force((n) => n + 1), 400); return () => clearInterval(id); }, []);
  const t = aiTimings;
  if (!t.total && !t.record && !t.stt) return null;
  return (
    <div style={{ position: 'fixed', top: 54, left: 12, zIndex: 30, background: 'rgba(0,0,0,0.72)', color: '#fff',
      font: '12px/1.5 ui-monospace, monospace', padding: '8px 12px', borderRadius: 10, pointerEvents: 'none' }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>last turn (ms)</div>
      <div>🎙 record {t.record || 0}</div>
      <div>👂 stt {t.stt || 0}</div>
      <div>🤔 think {t.think || 0}</div>
      <div>🔊 tts {t.tts || 0}</div>
      <div style={{ borderTop: '1px solid #555', marginTop: 3, paddingTop: 3, fontWeight: 700 }}>⏱ total {t.total || 0}</div>
    </div>
  );
}

// ─── Listening / Thinking indicator — cartoon THOUGHT CLOUD over the frog ───
// (2026-07-02, Polina): moved from below the frog to above his head, restyled
// as a classic comic thought cloud (puffy outline + trailing dots down toward
// the head). Drawn inline as SVG — no image asset, scales with the UI, and the
// border color still signals the mode (cyan = listening, gold = thinking).
const CLOUD_TOP = -92; // px above the frog frame — tune to sit over the head

// Overlapping circles form the cloud. Drawn twice: stroked (outline), then
// filled slightly smaller (erases the strokes inside the union) → one clean
// puffy silhouette.
const CLOUD_PUFFS = [
  [62, 74, 34], [104, 50, 40], [152, 52, 38], [192, 72, 30],
  [128, 84, 44], [78, 90, 30], [172, 90, 28],
];

function ThinkingBubble({ listening, thinking, speaking }) {
  if (speaking) return null;
  const mode = listening ? 'listen' : thinking ? 'think' : null;
  if (!mode) return null;
  const label = mode === 'listen' ? "I'm listening…" : 'Thinking…';
  const emoji = mode === 'listen' ? '👂' : '💭';
  const color = mode === 'listen' ? BRAND.cyan : BRAND.gold;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0 }}
      style={{ position: 'absolute', top: CLOUD_TOP, left: '50%', marginLeft: -125, width: 250,
        display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 3, pointerEvents: 'none' }}>
      {/* The cloud, gently breathing */}
      <motion.div animate={{ scale: [1, 1.045, 1] }} transition={{ repeat: Infinity, duration: 2 }}
        style={{ position: 'relative', width: 250, height: 130 }}>
        <svg width="250" height="130" viewBox="0 0 250 130" style={{ display: 'block', filter: 'drop-shadow(0 5px 10px rgba(0,0,0,0.18))' }}>
          <g fill={BRAND.white} stroke={color} strokeWidth="5">
            {CLOUD_PUFFS.map(([cx, cy, r], i) => <circle key={i} cx={cx} cy={cy} r={r} />)}
          </g>
          <g fill={BRAND.white}>
            {CLOUD_PUFFS.map(([cx, cy, r], i) => <circle key={i} cx={cx} cy={cy} r={r - 3} />)}
          </g>
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 8, paddingTop: 6 }}>
          <motion.span animate={{ scale: [1, 1.25, 1] }} transition={{ repeat: Infinity, duration: 1 }}
            style={{ fontSize: 26 }}>{emoji}</motion.span>
          <span style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 20, color: BRAND.teal }}>{label}</span>
        </div>
      </motion.div>
      {/* Trailing thought dots, stepping down toward the frog's head */}
      {[15, 10].map((size, i) => (
        <motion.div key={size}
          animate={{ opacity: [0.35, 1, 0.35] }}
          transition={{ repeat: Infinity, duration: 1.2, delay: i * 0.35 }}
          style={{ width: size, height: size, borderRadius: '50%', background: BRAND.white,
            border: `3px solid ${color}`, marginTop: i === 0 ? -4 : 5, marginLeft: (i + 1) * 14 }} />
      ))}
    </motion.div>
  );
}

// ─── Responsive scale-to-fit (2026-07-02 mobile pass) ───
// The game is laid out on a fixed ~1500×920 design canvas (600px card + frog
// at +200px). Instead of reflowing every absolute position for small screens,
// we scale the whole canvas to fit the viewport — same proportions on a
// Chromebook, iPad, or iPhone (the three POC target devices). Desktop = 1.0.
const DESIGN_W = 1500;
const DESIGN_H = 920;

function useViewportScale() {
  const calc = () => Math.min(1, window.innerWidth / DESIGN_W, window.innerHeight / DESIGN_H);
  const [scale, setScale] = useState(calc);
  useEffect(() => {
    const onResize = () => setScale(calc());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return scale;
}

// Full-viewport layer that scales its content about the center. Flex-centers
// flow content (start/complete screens); absolutely-positioned children
// (game card, frog) keep their own coordinates and just scale with the layer.
function Scaled({ scale, children }) {
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 1,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      transform: `scale(${scale})`, transformOrigin: '50% 50%' }}>
      {children}
    </div>
  );
}

// Portrait phones: the landscape canvas would shrink to unusable — ask for a
// rotate instead (standard for kids' games).
function RotateHint() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const check = () => setShow(window.innerHeight > window.innerWidth && window.innerWidth < 720);
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(9,111,120,0.94)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 14, color: '#fff', fontFamily: "'Nunito', sans-serif", textAlign: 'center', padding: 24 }}>
      <motion.div animate={{ rotate: [0, 90, 90, 0] }} transition={{ repeat: Infinity, duration: 2.4, times: [0, 0.35, 0.7, 1] }}
        style={{ fontSize: 64 }}>📱</motion.div>
      <div style={{ fontSize: 26, fontWeight: 900 }}>Turn your device sideways!</div>
      <div style={{ fontSize: 17, fontWeight: 600, opacity: 0.9 }}>The farm fits better in landscape.</div>
    </div>
  );
}

// ─── Little Sponges Logo ───
function LittleSpongesLogo() {
  return (
    <img src={LOGO_SRC} alt="Little Sponges®"
      style={{ height: 40, objectFit: 'contain', userSelect: 'none' }} />
  );
}

// ─── Farm Background (inline SVG) ───
// ─── Farm Background ───
// Now a real picture (public/images/farm-background.png, swapped in 2026-06-09).
// The old code-drawn SVG scene is kept below as FarmBackgroundDrawn for easy
// revert — delete it once the picture is confirmed good.
function FarmBackground() {
  // Adventures without background art yet (Playground) fall back to a plain
  // sky-and-grass scene — the drawn farm scene below has a barn, which would
  // look wrong outside Farm. Swap in real art via the pack's `background`
  // as soon as Natalya supplies it.
  if (!ADVENTURE.background) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: 'linear-gradient(180deg, #7EC8E3 0%, #ADE4F7 55%, #D4F1FF 100%)' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '26vh',
          background: 'linear-gradient(180deg, #6DBE45 0%, #4A9E26 100%)',
          borderTopLeftRadius: '50% 30%', borderTopRightRadius: '50% 30%' }} />
      </div>
    );
  }
  return (
    <img
      src={ADVENTURE.background}
      alt=""
      style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        objectFit: 'cover', zIndex: 0, pointerEvents: 'none' }}
    />
  );
}

// eslint-disable-next-line no-unused-vars
function FarmBackgroundDrawn() {
  return (
    <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice"
      style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 0, pointerEvents: 'none' }}>
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7EC8E3" /><stop offset="60%" stopColor="#ADE4F7" /><stop offset="100%" stopColor="#D4F1FF" />
        </linearGradient>
        <linearGradient id="grass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6DBE45" /><stop offset="100%" stopColor="#4A9E26" />
        </linearGradient>
        <linearGradient id="hill1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7ECF55" /><stop offset="100%" stopColor="#5BB82F" />
        </linearGradient>
        <linearGradient id="hill2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6DC048" /><stop offset="100%" stopColor="#4FA828" />
        </linearGradient>
        <linearGradient id="barnWall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#D94040" /><stop offset="100%" stopColor="#B33030" />
        </linearGradient>
        <linearGradient id="barnRoof" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8B4513" /><stop offset="100%" stopColor="#6B3410" />
        </linearGradient>
      </defs>
      <rect width="1440" height="900" fill="url(#sky)" />
      <circle cx="1280" cy="120" r="70" fill="#FFE066" opacity="0.9" />
      <circle cx="1280" cy="120" r="55" fill="#FFD633" />
      <g opacity="0.85"><ellipse cx="250" cy="100" rx="80" ry="35" fill="white" /><ellipse cx="310" cy="90" rx="60" ry="30" fill="white" /><ellipse cx="200" cy="95" rx="55" ry="28" fill="white" /></g>
      <g opacity="0.7"><ellipse cx="750" cy="140" rx="70" ry="28" fill="white" /><ellipse cx="800" cy="130" rx="55" ry="24" fill="white" /><ellipse cx="710" cy="135" rx="45" ry="22" fill="white" /></g>
      <g opacity="0.6"><ellipse cx="1050" cy="80" rx="60" ry="25" fill="white" /><ellipse cx="1100" cy="72" rx="50" ry="22" fill="white" /></g>
      <ellipse cx="300" cy="560" rx="450" ry="160" fill="url(#hill2)" />
      <ellipse cx="1100" cy="580" rx="500" ry="170" fill="url(#hill2)" />
      <ellipse cx="700" cy="620" rx="900" ry="180" fill="url(#hill1)" />
      <rect x="0" y="620" width="1440" height="280" fill="url(#grass)" />
      <g transform="translate(80, 380)">
        <rect x="0" y="60" width="160" height="130" rx="4" fill="url(#barnWall)" />
        <polygon points="-15,60 80,-10 175,60" fill="url(#barnRoof)" />
        <rect x="65" y="-18" width="30" height="14" rx="3" fill="#6B3410" />
        <rect x="50" y="110" width="60" height="80" rx="30 30 0 0" fill="#6B3410" />
        <rect x="55" y="115" width="50" height="75" rx="25 25 0 0" fill="#3B1E08" />
        <line x1="80" y1="115" x2="80" y2="190" stroke="#6B3410" strokeWidth="3" />
        <line x1="55" y1="155" x2="105" y2="155" stroke="#6B3410" strokeWidth="3" />
        <rect x="15" y="90" width="25" height="22" rx="2" fill="#FFE8A0" opacity="0.8" />
        <rect x="120" y="90" width="25" height="22" rx="2" fill="#FFE8A0" opacity="0.8" />
        <circle cx="80" cy="35" r="14" fill="#FFE8A0" opacity="0.7" />
      </g>
      <g transform="translate(260, 370)">
        <rect x="0" y="30" width="45" height="140" rx="6" fill="#C8C8C8" />
        <ellipse cx="22" cy="30" rx="23" ry="10" fill="#B0B0B0" />
        <rect x="5" y="0" width="35" height="35" rx="4" fill="#A0A0A0" />
        <ellipse cx="22" cy="4" rx="18" ry="7" fill="#909090" />
      </g>
      <g opacity="0.85">
        {[100, 220, 340, 460, 580, 860, 980, 1100, 1220, 1340].map((x) => (
          <g key={x}><rect x={x} y="610" width="12" height="70" rx="2" fill="#8B6914" /><ellipse cx={x + 6} cy="610" rx="8" ry="4" fill="#A07A1A" /></g>
        ))}
        <rect x="90" y="625" width="480" height="8" rx="3" fill="#A07A1A" />
        <rect x="90" y="650" width="480" height="8" rx="3" fill="#A07A1A" />
        <rect x="850" y="625" width="510" height="8" rx="3" fill="#A07A1A" />
        <rect x="850" y="650" width="510" height="8" rx="3" fill="#A07A1A" />
      </g>
      <g transform="translate(1300, 400)">
        <rect x="15" y="50" width="20" height="80" rx="4" fill="#7A5C2E" />
        <ellipse cx="25" cy="30" rx="45" ry="50" fill="#3D8B37" />
        <ellipse cx="15" cy="40" rx="35" ry="40" fill="#4A9E26" />
      </g>
      <g transform="translate(1370, 430)">
        <rect x="10" y="40" width="16" height="60" rx="3" fill="#7A5C2E" />
        <ellipse cx="18" cy="22" rx="35" ry="40" fill="#3D8B37" />
      </g>
      <g opacity="0.7">
        <circle cx="150" cy="680" r="5" fill="#FF6B8A" /><circle cx="165" cy="690" r="4" fill="#FFD633" />
        <circle cx="140" cy="695" r="4" fill="#FF6B8A" /><circle cx="900" cy="670" r="5" fill="#FFD633" />
        <circle cx="920" cy="682" r="4" fill="#FF6B8A" /><circle cx="885" cy="685" r="4" fill="#FFD633" />
        <circle cx="1200" cy="700" r="4" fill="#FF6B8A" /><circle cx="1215" cy="695" r="5" fill="#FFD633" />
        <circle cx="500" cy="710" r="4" fill="#FF6B8A" /><circle cx="515" cy="705" r="5" fill="#FFD633" />
      </g>
      <g transform="translate(270, 520)"><ellipse cx="0" cy="0" rx="25" ry="18" fill="#D4A843" /></g>
      <g transform="translate(320, 530)"><ellipse cx="0" cy="0" rx="22" ry="16" fill="#D4A843" /></g>
      <path d="M 700,900 Q 680,800 720,720 Q 750,660 700,620" fill="none" stroke="#C4A265" strokeWidth="40" opacity="0.3" strokeLinecap="round" />
    </svg>
  );
}

// ─── Fireworks ───
// Full-screen celebration overlay for correct answers (replaces the old 🎉 emoji).
// Pure transform/opacity animations (GPU-composited) — cheap even on weak school
// laptops, no video asset and no chroma-keying. Loops while the celebrate phase
// is active; pointer-events disabled so it never blocks taps.
function Fireworks({ bursts = 7 }) {
  const [config] = useState(() =>
    Array.from({ length: bursts }, (_, i) => ({
      left: 8 + Math.random() * 84,           // % of screen width
      top: 10 + Math.random() * 55,           // % of screen height
      delay: i * 0.22,
      particles: Array.from({ length: 12 }, (_, p) => {
        const angle = (p / 12) * Math.PI * 2;
        const dist = 70 + Math.random() * 50;
        return {
          dx: Math.cos(angle) * dist,
          dy: Math.sin(angle) * dist,
          color: ['#F7AD00', '#FFC940', '#FF6B35', '#5BC0DE', '#FF6B8A', '#FFFFFF'][p % 6],
        };
      }),
    }))
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 20, pointerEvents: 'none', overflow: 'hidden' }}>
      {config.map((burst, i) => (
        <div key={i} style={{ position: 'absolute', left: `${burst.left}%`, top: `${burst.top}%` }}>
          {/* Central flash */}
          <motion.div
            initial={{ scale: 0, opacity: 1 }}
            animate={{ scale: [0, 1.6, 0], opacity: [1, 0.9, 0] }}
            transition={{ duration: 0.5, delay: burst.delay, repeat: Infinity, repeatDelay: 1.4 }}
            style={{ position: 'absolute', width: 26, height: 26, marginLeft: -13, marginTop: -13,
              borderRadius: '50%', background: 'white', boxShadow: '0 0 20px 8px rgba(255,255,255,0.8)' }}
          />
          {/* Radiating sparks */}
          {burst.particles.map((p, j) => (
            <motion.div key={j}
              initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
              animate={{ x: p.dx, y: p.dy, scale: 0.15, opacity: 0 }}
              transition={{ duration: 1.0, delay: burst.delay, ease: 'easeOut', repeat: Infinity, repeatDelay: 0.9 }}
              style={{ position: 'absolute', width: 9, height: 9, marginLeft: -4.5, marginTop: -4.5,
                borderRadius: '50%', background: p.color, boxShadow: `0 0 6px 2px ${p.color}` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Star Row ───
// Renders `total` slots so the child sees how many stars are still possible.
// Earned stars (i < score) animate in gold; unearned slots stay greyed out.
function StarRow({ score, total = QUESTIONS_PER_GAME }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', minHeight: 36 }}>
      {Array.from({ length: total }, (_, i) => {
        const earned = i < score;
        return (
          <motion.span
            key={i}
            initial={earned ? { scale: 0, rotate: -180 } : false}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 15 }}
            style={{
              fontSize: 32,
              opacity: earned ? 1 : 0.25,
              filter: earned ? 'none' : 'grayscale(100%)',
              transition: 'opacity 200ms, filter 200ms',
            }}
          >
            ⭐
          </motion.span>
        );
      })}
    </div>
  );
}

// ─── Frog Video Avatar ───
const FROG_VIDEOS = { idle: '/videos/frog-idle.mp4', talking: '/videos/frog-talking.mp4', correct: '/videos/frog-correct.mp4', tryagain: '/videos/frog-tryagain.mp4', thinking: '/videos/frog-thinking.mp4' };
// Per-state scale factor. All four source videos are spatially normalized at
// the ffmpeg level (bottom y=1820, center x=540 in their 1080x1920 frame), so
// no per-state scaling is needed. idle/talking/tryagain: body height ~1100px.
// correct: re-normalized 2026-06-09 at 0.962× (body ~998px) because the jump
// apex needs the headroom — at 1100px the head/hands clipped at the frame top.
// LESSON for any future video (incl. Misha): normalize to fit the FULL motion
// range across all frames, not the standing pose. Originals preserved in
// public/videos/_originals/.
const FROG_SCALES = { correct: 1.00, tryagain: 1.00, idle: 1.00, talking: 1.00 };

// Celebration variety (2026-07-16, Natalya): the frog picks one of these at
// random on each correct answer. frog-correct (the original jump) stays and is
// the fallback; frog-dance / frog-spin are added clips. Drop the normalized
// files in public/videos/ with these names and they join the rotation
// automatically — nothing else to change. A clip that fails to load (e.g. not
// added yet) is dropped from the pool at runtime and falls back to the jump,
// so this is safe to ship before all three files exist.
const FROG_CELEBRATIONS = [
  '/videos/frog-correct.mp4', // the jump — keep
  '/videos/frog-dance.mp4',   // happy dance — trimmed 2026-08-11 to its clean segment
  '/videos/frog-spin.mp4',    // joyful spin — REGENERATED 2026-08-12 (Kling, landscape
  // source so the arms stay in frame; the first Kling take clipped the arms on
  // 73/169 frames). Preprocessed: re-keyed from Kling's desaturated blue onto
  // the app's key blue 0x37ADEE (shadow removed), normalized to feet y=1820 /
  // center x=540 / body ~1060px, verified 0 edge-touching frames. Raw source:
  // _originals/frog-spin-kling2-raw.mp4.
  '/videos/frog-clap.mp4',    // clap — ADDED 2026-08-12 per Natalya ("add the clap
  // as a fourth option; 3 moves felt repetitive" — Mark's request). Kling
  // landscape source, same re-key + normalization pipeline as the spin,
  // verified 0 edge-touching frames of 169. Raw: _originals/frog-clap-kling-raw.mp4.
  // This FOUR-move set (jump/dance/spin/clap) is final for the POC.
];
const FROG_CELEBRATION_FALLBACK = '/videos/frog-correct.mp4';

// ─── Mishka (2026-10-27) ───
// The frog is the English tutor; every other language gets Mishka, the Little
// Sponges bear puppet. Same five states and the same normalization as the
// frog (feet y=1820, standing body ~1100px, centered), so the engine treats
// her exactly like the frog. Generated in Kling from a front-facing still cut
// from Natalya's "Mishka and Frog" video; raws + start frames + the
// trim/normalize scripts live in public/videos/_originals/mishka/.
// Her celebrations are WIDE clips (1600x1920, standing center x=800): her arms
// spread much wider than the frog's, and the wide frame keeps her full size
// instead of shrinking mid-move. The avatar box is square, so a 1600x1920
// clip still fits by height: she renders at the same size as in her
// 1080x1920 clips, and her paws use the box's spare side room, passing BEHIND
// the card (the card column sits one layer above the avatar).
const MISHKA_VIDEOS = { idle: '/videos/mishka-idle.mp4', talking: '/videos/mishka-talking.mp4', correct: '/videos/mishka-jump.mp4', tryagain: '/videos/mishka-tryagain.mp4', thinking: '/videos/mishka-thinking.mp4' };
const MISHKA_CELEBRATIONS = [
  '/videos/mishka-jump.mp4',  // one big jump, sped up 1.4x — the fallback
  '/videos/mishka-dance.mp4', // happy bouncing with arms up
  '/videos/mishka-spin.mp4',  // full turn, then a cheer
  '/videos/mishka-clap.mp4',  // claps, then a wave
];
const CHARACTERS = {
  // id = voice + audio folder: /audio-gemini/<id>/ and /api/speak character.
  frog:   { id: 'frog',   videos: FROG_VIDEOS,   celebrations: FROG_CELEBRATIONS,   fallback: FROG_CELEBRATION_FALLBACK },
  mishka: { id: 'mishka', videos: MISHKA_VIDEOS, celebrations: MISHKA_CELEBRATIONS, fallback: '/videos/mishka-jump.mp4' },
};
// English -> frog; any other language pack -> Mishka.
// ?character=frog|mishka overrides it for testing.
const CHARACTER_PARAM = typeof window !== 'undefined'
  ? new URLSearchParams(window.location.search).get('character') : null;
const CHARACTER = CHARACTERS[CHARACTER_PARAM]
  || CHARACTERS[(ADVENTURE.langId && ADVENTURE.langId !== 'en') ? 'mishka' : 'frog'];

const failedFrogSrc = new Set();
function pickCelebration() {
  const ok = CHARACTER.celebrations.filter((s) => !failedFrogSrc.has(s));
  const pool = ok.length ? ok : [CHARACTER.fallback];
  return pool[Math.floor(Math.random() * pool.length)];
}

function FrogAvatar({ size = 360, state = 'idle' }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const [currentSrc, setCurrentSrc] = useState(CHARACTER.videos.idle);
  // On each 'correct' celebration, lock in one random clip for the duration of
  // that celebration; clear it when we leave 'correct' so the next one re-picks.
  const celebrationSrcRef = useRef(null);
  if (state === 'correct') {
    if (!celebrationSrcRef.current) celebrationSrcRef.current = pickCelebration();
  } else {
    celebrationSrcRef.current = null;
  }
  const videoSrc = state === 'correct'
    ? (celebrationSrcRef.current || CHARACTER.fallback)
    : (CHARACTER.videos[state] || CHARACTER.videos.idle);

  useEffect(() => { if (videoSrc !== currentSrc) setCurrentSrc(videoSrc); }, [videoSrc, currentSrc]);
  useEffect(() => { if (videoRef.current) { videoRef.current.currentTime = 0; videoRef.current.play().catch(() => {}); } }, [currentSrc]);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    // The blue-screen key below runs per-pixel on the CPU every frame, so its
    // cost scales with (pixel count × frame rate). Two levers keep that cheap
    // enough for the low-end school laptops this app targets:
    //
    //  1. MAX_W caps the processing resolution. Source videos are 1080×1920
    //     (~2.07M px/frame); processing at 480px wide is ~410k px — about 5×
    //     fewer pixels per frame. The canvas is scaled up by CSS for display,
    //     which is imperceptible for a cartoon frog. Lower this to 360 if a
    //     weak machine still struggles; raise it for crisper output.
    //  2. FRAME_MS throttles to ~30fps. requestAnimationFrame fires at the
    //     display refresh (60–120Hz); 30fps is plenty for this loop animation
    //     and roughly halves CPU on 60Hz+ screens.
    const MAX_W = 480;
    const FRAME_MS = 1000 / 30;

    // Single owned loop. `cancelled` short-circuits any in-flight callback the
    // instant the effect tears down, and the rAF handle is always the most
    // recent one, so cleanup fully stops the loop. This is the fix for the
    // freeze: the previous version could start a second drawFrame loop per
    // video-source change (the 'play' listener AND the !paused check both
    // started one) while only tracking the latest handle — every frog state
    // change (idle/talking/correct/tryagain) orphaned a chroma-key loop that
    // could never be cancelled, so CPU climbed until the UI froze.
    let cancelled = false;
    let last = 0;

    const drawFrame = (now) => {
      if (cancelled) return;
      rafRef.current = requestAnimationFrame(drawFrame);
      if (now - last < FRAME_MS) return;            // throttle to ~30fps
      last = now;

      if (video.readyState < 2) return;             // not enough data yet
      const vw = video.videoWidth, vh = video.videoHeight;
      if (!vw || !vh) return;

      // Pixel budget = a 480-wide 9:16 frame. Identical to MAX_W for the
      // 1080x1920 clips; Mishka's wide 1600x1920 celebrations get the same
      // pixel count (same CPU cost) instead of being squeezed to 480 wide.
      const scale = Math.min(1, Math.sqrt((MAX_W * MAX_W * 16 / 9) / (vw * vh)));
      const w = Math.max(1, Math.round(vw * scale));
      const h = Math.max(1, Math.round(vh * scale));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }

      ctx.drawImage(video, 0, 0, w, h);
      const imageData = ctx.getImageData(0, 0, w, h);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        const r = d[i], g = d[i + 1], b = d[i + 2];
        const blueness = b - Math.max(r, g);
        if (b > 100 && blueness > 50) { d[i + 3] = 0; }
        else if (b > 60 && blueness > 20) { d[i + 3] = Math.round((1 - Math.min(1, (blueness - 20) / 40)) * 255); }
      }
      ctx.putImageData(imageData, 0, 0);
    };

    rafRef.current = requestAnimationFrame(drawFrame);
    return () => {
      cancelled = true;
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, [currentSrc]);

  const scale = FROG_SCALES[state] ?? 1;
  return (
    <div style={{ width: size, height: size, position: 'relative', flexShrink: 0, overflow: 'hidden' }}>
      <video ref={videoRef} src={currentSrc} autoPlay loop muted playsInline
        onError={() => {
          // A celebration clip that can't load (e.g. not added yet) is dropped
          // from the rotation and we fall back to the known-good jump instantly.
          if (CHARACTER.celebrations.includes(currentSrc) && currentSrc !== CHARACTER.fallback) {
            failedFrogSrc.add(currentSrc);
            celebrationSrcRef.current = CHARACTER.fallback;
            setCurrentSrc(CHARACTER.fallback);
          }
        }}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
      <canvas ref={canvasRef}
        style={{ width: '100%', height: '100%', objectFit: 'contain',
          transform: `scale(${scale})`, transformOrigin: '50% 100%',
          transition: 'transform 180ms ease-out' }} />
    </div>
  );
}

// ─── Speech Bubble ───
function SpeechBubble({ text, visible }) {
  if (!visible || !text) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      style={{ maxWidth: 300, fontFamily: "'Nunito', sans-serif", fontSize: 20, fontWeight: 700, lineHeight: 1.4,
        color: BRAND.white, textAlign: 'center', textShadow: `2px 2px 4px ${BRAND.teal}` }}>
      {text}
    </motion.div>
  );
}

// ─── Repeat Banner ───
// The big echo target for "Repeat after me." Sits under the picture card in
// huge type with the key vocabulary word highlighted in pink — readable from
// across a classroom, and a clear visual event even for pre-readers.
function RepeatBanner({ target }) {
  if (!target) return null;
  const { sentence, highlight } = target;
  const idx = highlight ? sentence.toLowerCase().indexOf(highlight.toLowerCase()) : -1;
  const parts = idx >= 0
    ? [sentence.slice(0, idx), sentence.slice(idx, idx + highlight.length), sentence.slice(idx + highlight.length)]
    : [sentence, '', ''];
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7, y: 12 }}
      animate={{ opacity: 1, scale: [0.7, 1.06, 1], y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.45 }}
      style={{ background: BRAND.white, border: `6px solid ${BRAND.gold}`, borderRadius: 22,
        padding: '16px 36px', boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
        fontFamily: "'Nunito', sans-serif", fontSize: 44, fontWeight: 900, lineHeight: 1.2,
        color: BRAND.teal, textAlign: 'center', maxWidth: 680 }}>
      {parts[0]}
      <span style={{ color: ACCENT }}>{parts[1]}</span>
      {parts[2]}
    </motion.div>
  );
}

// ─── "You said" strip (2026-10-20, Natalya tracker #12) ───
// What the frog heard, shown under the card right after each answer so the
// child (and a teacher watching) can compare it with what was asked. Words
// that match the expected answer turn green; the rest stay plain. A quiet
// extra for readers — the frog's speech bubble stays the frog's voice.
function HeardStrip({ text, expected = [] }) {
  if (!text) return null;
  const exp = new Set(expected.map((w) => cleanSpoken(String(w))).join(' ').split(' ').filter(Boolean));
  const isMatch = (w) => {
    const c = cleanSpoken(w);
    if (!c) return false;
    if (exp.has(c)) return true;
    if (c.length < 4) return false;
    for (const e of exp) if (e.length >= 4 && levenshtein(c, e) <= 1) return true;
    return false;
  };
  const words = text.split(/\s+/).filter(Boolean);
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      style={{ background: 'rgba(255,255,255,0.92)', borderRadius: 16, padding: '6px 18px',
        boxShadow: '0 3px 12px rgba(0,0,0,0.15)', fontFamily: "'Nunito', sans-serif",
        fontSize: 24, fontWeight: 800, color: BRAND.gray, maxWidth: 620, textAlign: 'center', lineHeight: 1.3 }}>
      <span style={{ fontSize: 18, fontWeight: 800, color: BRAND.teal, marginRight: 8 }}>
        {uiText('youSaid', 'You said:')}
      </span>
      {words.map((w, i) => (
        <span key={i} style={{ color: isMatch(w) ? BRAND.btnGreenDark : '#4A5560' }}>{w}{i < words.length - 1 ? ' ' : ''}</span>
      ))}
    </motion.div>
  );
}

// ─── Round Button ───
// `pulse` makes the button breathe and sends soft glowing rings rippling
// outward — attention cue for pre-readers who can't parse "Tap to talk!"
// (Natalya's request, 2026-06-09). Transform/opacity only, so it's cheap.
function RoundButton({ onClick, color, darkColor, size = 80, icon, label, disabled = false, pulse = false }) {
  const pulsing = pulse && !disabled;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        {pulsing && [0, 0.7].map((delay) => (
          <motion.div key={delay}
            initial={{ scale: 1, opacity: 0.55 }}
            animate={{ scale: 1.55, opacity: 0 }}
            transition={{ duration: 1.5, delay, repeat: Infinity, ease: 'easeOut' }}
            style={{ position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none',
              border: `4px solid ${color}`, boxShadow: `0 0 18px 6px ${color}80` }} />
        ))}
        <motion.button
          animate={pulsing ? { scale: [1, 1.07, 1] } : { scale: 1 }}
          transition={pulsing ? { duration: 1.4, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
          whileHover={!disabled ? { scale: 1.1 } : {}} whileTap={!disabled ? { scale: 0.9 } : {}}
          onClick={onClick} disabled={disabled}
          style={{ width: size, height: size, borderRadius: '50%', border: `4px solid ${darkColor}`,
            cursor: disabled ? 'default' : 'pointer',
            background: `radial-gradient(circle at 35% 35%, ${color}, ${darkColor})`,
            color: BRAND.white, fontSize: size * 0.4, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: pulsing
              ? `0 0 26px 9px ${color}55, 0 6px 16px ${darkColor}60, inset 0 2px 4px rgba(255,255,255,0.3)`
              : `0 6px 16px ${darkColor}60, inset 0 2px 4px rgba(255,255,255,0.3)`,
            opacity: disabled ? 0.5 : 1 }}>
          {icon}
        </motion.button>
      </div>
      {label && <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: 18, fontWeight: 800,
        color: BRAND.white, textShadow: `1px 1px 3px ${BRAND.teal}` }}>{label}</span>}
    </div>
  );
}

// ─── Mic Button ───
function MicButton({ listening, onClick, disabled }) {
  return (
    <RoundButton onClick={onClick} disabled={disabled} pulse={!listening}
      color={listening ? BRAND.red : ACCENT}
      darkColor={listening ? BRAND.redDark : ACCENT_DARK}
      size={120} icon={listening ? '⏹' : '🎤'}
      label={listening ? uiText('micListening', 'Listening...') : uiText('micTap', 'Tap to talk!')} />
  );
}

// ═══════════════════════════════════════════════════
// ─── MAIN APP ─────────────────────────────────────
// ═══════════════════════════════════════════════════

/*
 * Question tiers per card:
 *   'name'  → "What is this?" → expects animal word
 *   'color' → "What color is the [animal]?" → expects color word
 *   'count' → "How many [animals] do you see?" → expects number word
 *   'favorite' → open-ended at the very end
 */

export default function DemoApp() {
  const [deck, setDeck] = useState(() => buildDeck());
  const [exerciseIndex, setExerciseIndex] = useState(0);
  const [questionTier, setQuestionTier] = useState('name'); // name | color | count
  const [phase, setPhase] = useState('start'); // start | intro | respond | feedback | giveAnswer | celebrate | complete | favorite
  const [feedbackText, setFeedbackText] = useState('');
  // The sentence the child must echo after "Repeat after me." — rendered as a
  // big banner under the card (pre-readers get a clear visual event; readers
  // get large type with the key word highlighted). null = no banner.
  const [repeatTarget, setRepeatTarget] = useState(null); // { sentence, highlight }
  // Last thing the frog heard, for the "You said" strip (tracker #12).
  const [heardText, setHeardText] = useState('');
  // ✕ while auto-recording: false | 'saving' | 'done' (tracker #1). The
  // "Saving…" overlay itself is drawn by SessionRecorderButton (every screen).
  const exitSavingRef = useRef(false);
  const [correctIds, setCorrectIds] = useState(new Set());
  const [attemptNumber, setAttemptNumber] = useState(1);

  // Preload every card image AND the favorite-screen group image as soon as
  // the deck is built. By the time the child reaches card 2/3/4 the images
  // are already warm in the browser cache, so transitions are instant
  // instead of showing the white card frame followed by a streaming image.
  useEffect(() => {
    const urls = [...deck.map((c) => c.image),
      ...(ADVENTURE.hasFavorite ? [favImage()] : [])];
    for (const url of urls) {
      const img = new Image();
      img.src = url;
    }
  }, [deck]);

  // Game state ref (sync reads — no stale closures)
  const gameRef = useRef({
    exerciseIndex: 0, attemptNumber: 1, phase: 'start', questionTier: 'name',
  });
  const advanceTimerRef = useRef(null);
  const gameOverRef = useRef(false);
  const completionPlayedRef = useRef(false);
  const lastProcessedTranscriptId = useRef(0);
  // Favorite screen: 3-attempt counter (0 = no wrong answers yet)
  const favoriteAttemptsRef = useRef(0);
  // Favorite screen: has the frog already asked for English once? (AI mode)
  const favoriteEnglishPromptedRef = useRef(false);
  // Favorite screen: consecutive judge-API failures. A failed call must never
  // be spoken as "That's not an animal" (2026-08-11, Natalya's rabbit report).
  const favoriteJudgeFailsRef = useRef(0);
  const score = correctIds.size;

  const updateGame = useCallback((updates) => {
    Object.assign(gameRef.current, updates);
    if ('attemptNumber' in updates) setAttemptNumber(updates.attemptNumber);
    if ('exerciseIndex' in updates) setExerciseIndex(updates.exerciseIndex);
    if ('phase' in updates) setPhase(updates.phase);
    if ('questionTier' in updates) setQuestionTier(updates.questionTier);
  }, []);

  const { speak, speakText, stop: stopAudio, speaking } = useAudioPlayer();
  // Listening source (2026-07-02): universal STT (streaming Gemini Live with
  // turn-based server fallback) is now the DEFAULT for everyone — it's the
  // cross-browser fix (Web Speech never worked outside desktop Chrome, which
  // is why Natalya's phone "couldn't hear her"). Old paths stay reachable via
  // ?stt=hybrid / ?stt=browser for A/B and rollback.
  // All hooks are called (rules of hooks); only the selected one is wired up.
  const browserSTT = useSTT();
  const hybridSTT = useHybridSTT();
  const universalSTT = useUniversalSTT();
  const sttSource = STT_MODE === 'browser' ? browserSTT : STT_MODE === 'hybrid' ? hybridSTT : universalSTT;
  const { startListening, stopListening, listening, transcript, alternatives, transcriptId } = sttSource;
  const partialText = sttSource.partialText || '';
  // "You said" strip: show each finished answer; clear when the question moves on.
  useEffect(() => { if (transcript && transcript.trim()) setHeardText(transcript.trim()); }, [transcriptId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setHeardText(''); }, [exerciseIndex, questionTier]);
  useEffect(() => { if (phase === 'start' || phase === 'complete' || phase === 'favorite') setHeardText(''); }, [phase]);
  const transcribing = sttSource.transcribing || false;
  const sttTurn = sttSource.turn || 0;
  const sttSourceName = sttSource.source || STT_MODE;
  const [thinking, setThinking] = useState(false);
  const uiScale = useViewportScale();
  // Speak helper — CACHE-FIRST (agreed with Natalya, June 2026):
  //   1. If we already have a pre-generated line, play it (free + instant).
  //      speak() itself still falls back to /api/speak if the file is missing.
  //   2. Cache miss + AI mode → generate the line live via the AI.
  //   3. Cache miss + non-AI mode → keep the proven offline path (no AI call).
  const say = useCallback((path, text) => {
    if (path && resolveResponse(path).cached) return speak(path, text);
    if (AI_MODE && text) return speakText(text);
    return speak(path, text);
  }, [speak, speakText]);

  const exercise = deck[exerciseIndex] || deck[0];

  // Cancel timers
  const cancelTimers = useCallback(() => {
    if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
  }, []);

  // ─── Ask the current question based on tier ───
  const askQuestion = useCallback((tier, animal) => {
    cancelTimers();
    gameRef.current.flow = null; // never carry ladder state into a new question
    // Tell the listener what CLASS of answer is coming (rescues short words).
    // Name tier enumerates the small fixed vocabulary — isolated words like
    // "geese" transcribe fine in sentences but mangle alone ("gies"); knowing
    // the candidate set anchors them. The judge still verifies afterwards.
    setSttContext(tier === 'count' ? (ADVENTURE.sttCountContext || 'a number from one to ten')
      : tier === 'color' ? (ADVENTURE.sttColorContext || 'a color word')
      : (tier === 'yesno' || tier === 'feelings') ? (ADVENTURE.sttYesNoContext || 'a yes or no answer from a young child')
      : tier === 'list' ? (ADVENTURE.sttListContext || 'a young child listing color words')
      : ADVENTURE.sttNameContext);
    setFeedbackText('');
    setRepeatTarget(null);
    updateGame({ attemptNumber: 1, questionTier: tier, phase: 'respond' });

    // A language pack builds its own questions (farm-es); otherwise the
    // original English defaults below.
    const pq = tier !== 'favorite' ? packQ(animal, tier) : null;
    if (pq) {
      speak(pq.audio, pq.text);
    } else if (tier === 'name') {
      // A card may carry its own question (Playground family: "Who is this?").
      // Default: multi cards "What are these?", singles "What is this?".
      const ask = animal.ask || (animal.count > 1
        ? { audio: '/audio/what-are-these.mp3', text: 'What are these?' }
        : { audio: '/audio/what-is-this.mp3', text: 'What is this?' });
      speak(ask.audio, ask.text);
    } else if (tier === 'color') {
      const askWord = animal.count > 1 ? `the ${animal.plural}` : `the ${animal.word}`;
      speak(`/audio/${animal.id}-color-ask.mp3`, `What color ${animal.count > 1 ? 'are' : 'is'} ${askWord}?`);
    } else if (tier === 'count') {
      speak(`/audio/${animal.id}-count-ask.mp3`, `How many ${animal.plural} do you see?`);
    } else if (tier === 'favorite') {
      speak(favAskAudio(), favQuestion());
    }
  }, [speak, cancelTimers, updateGame]);

  // ─── Start card (begins with the card's FIRST tier — 'name' for every
  // Farm/Playground card; School supply cards open on 'yesno', the
  // do-you-like-to-learn card on 'feelings') ───
  const startExercise = useCallback(() => {
    if (gameOverRef.current) return;
    gameRef.current.creditedTiers = null; // bonus credit never carries across cards
    const card = deck[gameRef.current.exerciseIndex];
    askQuestion((card.tiers && card.tiers[0]) || 'name', card);
  }, [askQuestion, deck]);

  // Handle "start" button tap — wait for greeting to finish before transitioning
  // so the first prompt doesn't cut off the greeting audio.
  const handleStart = useCallback(() => {
    // iOS/Safari: audio must be unlocked inside a user gesture. This tap is
    // the one guaranteed gesture, so unlock the shared AudioContext here —
    // every later programmatic play (questions, feedback) rides on it.
    unlockAudio();
    // Auto-record (WordPress lesson links): this tap is also the gesture that
    // lets the recorder grab the mic.
    startAutoRecording();
    // Name-capture opener (School, 2026-08-17): the pack's greeting IS the
    // opener question ("…My name is Frog. What is your name?"), so the flow
    // goes greeting → 'opener' phase (mic for the name) instead of → intro.
    const op = ADVENTURE.opener;
    const greet = op ? op.greeting
      : packLine('greeting', '/audio/greeting.mp3', "Hello! Let's look at the pictures.");
    speak(greet.audio, greet.text).then(() => {
      if (gameOverRef.current) return;
      if (op) {
        setSttContext(op.sttContext || "a young child's first name");
        gameRef.current.phase = 'opener';
        setPhase('opener');
        return;
      }
      gameRef.current.phase = 'intro';
      setPhase('intro');
    });
  }, [speak]);

  // Auto-start on exercise change
  useEffect(() => {
    if (phase === 'intro' && !gameOverRef.current) {
      const timer = setTimeout(startExercise, 800);
      return () => clearTimeout(timer);
    }
  }, [exerciseIndex, phase, startExercise]);

  // ─── Advance: move to next tier, next card, or complete ───
  const advanceToNextCard = useCallback((delay = 2000) => {
    cancelTimers();
    advanceTimerRef.current = setTimeout(() => {
      if (gameOverRef.current) return;
      const idx = gameRef.current.exerciseIndex;
      if (idx < deck.length - 1) {
        const next = idx + 1;
        // Reset tier to the NEXT card's first tier AT THE SAME TIME as the
        // index changes so the on-screen label doesn't briefly show the
        // previous card's question (e.g. "What color is the chicken?") before
        // flipping. (First tier is 'name' everywhere except School's yesno /
        // feelings cards.)
        const nextCard = deck[next];
        updateGame({ exerciseIndex: next, phase: 'intro',
          questionTier: (nextCard.tiers && nextCard.tiers[0]) || 'name' });
      } else if (!ADVENTURE.hasFavorite) {
        // All cards done, no favorite flow in this adventure → straight to
        // the completion screen (Playground: naming only, per the script).
        gameOverRef.current = true;
        updateGame({ phase: 'complete' });
      } else {
        // All cards done → ask favorite question
        gameOverRef.current = true;
        favoriteAttemptsRef.current = 0;
        favoriteEnglishPromptedRef.current = false;
        favoriteJudgeFailsRef.current = 0;
        setSttContext(ADVENTURE.sttFavoriteContext);
        updateGame({ phase: 'favorite', questionTier: 'favorite' });
        setFeedbackText('');
        speak(favAskAudio(), favQuestion());
      }
    }, delay);
  }, [deck.length, cancelTimers, updateGame, speak]);

  const advanceToNextTier = useCallback((currentTier, delay = 2000) => {
    cancelTimers();
    advanceTimerRef.current = setTimeout(() => {
      if (gameOverRef.current) return;
      const animal = deck[gameRef.current.exerciseIndex];
      // Each card carries its own `tiers` array set in buildDeck:
      //   multi cards   → ['name', 'color', 'count']
      //   full singles  → ['name', 'color']
      //   name-only     → ['name']         (turkey + one random single per round)
      const tiers = animal.tiers || ['name', 'color', ...(animal.count > 1 ? ['count'] : [])];
      // Skip tiers already credited by a compound answer ("I see two pink
      // pigs" starred color+count in advance) — never re-ask what the child
      // just said.
      const credited = gameRef.current.creditedTiers;
      let nextIndex = tiers.indexOf(currentTier) + 1;
      while (nextIndex < tiers.length && credited && credited.has(tiers[nextIndex])) nextIndex += 1;
      if (nextIndex < tiers.length) {
        askQuestion(tiers[nextIndex], animal);
      } else {
        advanceToNextCard(0);
      }
    }, delay);
  }, [cancelTimers, deck, askQuestion, advanceToNextCard]);

  // Play completion audio
  useEffect(() => {
    if (phase === 'complete' && !completionPlayedRef.current) {
      completionPlayedRef.current = true;
      // Personalized closing (School, 2026-08-17): 3+ stars → "You are a good
      // student, [name]!" with the name captured at the opener. Named lines go
      // through live Puck TTS (/api/speak, S3-cached per name); no name →
      // the pre-baked nameless line. Below the star threshold the standard
      // closing lines below play, exactly as before.
      const closing = ADVENTURE.closing;
      if (closing && score >= closing.minStars) {
        const name = gameRef.current.childName || '';
        const text = closing.line(name);
        logEvent('closing-personalized', { name: name || '(none)', score });
        const p = name ? speakText(text) : speak(closing.audioNoName, text);
        Promise.resolve(p).catch(() => {}).then(() => finishAutoRecording(score, 'complete'));
        return;
      }
      let p;
      if (score >= Math.ceil(QUESTIONS_PER_GAME * 0.7)) {
        const l = packLine('completeGreat', '/audio/complete-great.mp3', 'Great job. You did amazing today.');
        p = speak(l.audio, l.text);
      } else {
        const l = packLine('completeGoodTry', '/audio/complete-good-try.mp3', 'Good try. You did amazing today.');
        p = speak(l.audio, l.text);
      }
      // Auto-record: stop once the closing line has played, then send.
      Promise.resolve(p).catch(() => {}).then(() => finishAutoRecording(score, 'complete'));
    }
  }, [phase, speak, score]);

  // ─── Handle speech transcript ───
  useEffect(() => {
    if (!transcriptId || (gameOverRef.current && gameRef.current.phase !== 'favorite')) return;

    // ── OPENER — unscored name capture (School, 2026-08-17). Handled before
    // the phase/empty-transcript guards: 'opener' is its own phase and a
    // silent turn still matters (script: "Says nothing → Frog moves on").
    if (gameRef.current.phase === 'opener' && ADVENTURE.opener) {
      if (transcriptId <= lastProcessedTranscriptId.current) return;
      lastProcessedTranscriptId.current = transcriptId;
      const op = ADVENTURE.opener;
      let name = '';
      if (transcript) {
        // "my name is carlos" / "i'm carlos" / bare "carlos" → "Carlos".
        const words = cleanSpoken(transcript)
          .replace(/^(?:hi|hello|um|uh)\s+/, '')
          .replace(/^(?:my name is|my name's|my names|i am|i'm|im|it is|it's|its|this is)\s+/, '')
          .split(/\s+/)
          .filter((w) => w && !['my', 'name', 'is', 'frog'].includes(w));
        name = words.slice(0, 2)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      }
      logEvent('opener-name', { turn: sttTurn, heard: transcript, name: name || '(none)' });
      const toGame = () => {
        if (gameOverRef.current) return;
        setFeedbackText('');
        updateGame({ phase: 'intro' });
      };
      if (name) {
        gameRef.current.childName = name; // reused by the personalized closing
        setFeedbackText(op.greatName.text);
        say(op.greatName.audio, op.greatName.text).then(toGame);
      } else {
        toGame(); // any answer is accepted; none at all just moves on
      }
      return;
    }

    // Empty transcripts (silence) matter in AI mode — the frog nudges the child
    // without burning an attempt. Non-AI mode keeps the old ignore behavior.
    if (!transcript && !(AI_MODE && gameRef.current.questionTier !== 'favorite')) return;
    if (transcriptId <= lastProcessedTranscriptId.current) return;
    if (gameRef.current.phase !== 'respond' && gameRef.current.phase !== 'favorite') {
      console.log(`[BLOCKED] phase=${gameRef.current.phase}`);
      return;
    }
    lastProcessedTranscriptId.current = transcriptId;

    // ── YES/NO + FEELINGS tiers (School, 2026-08-17) — fully local, shared by
    // AI and non-AI modes: both answers are correct by design, so no judge is
    // needed. yesno runs the same two-step sentence ladder as the card game
    // (prompt with the banner → final teaching reveal); feelings gets Frog's
    // scripted reaction (Yay! / head down) and both earn the star.
    if (gameRef.current.questionTier === 'yesno' || gameRef.current.questionTier === 'feelings') {
      const g = gameRef.current;
      const tier = g.questionTier;
      const card = deck[g.exerciseIndex] || deck[0];
      const flow = g.flow || (g.flow = { stage: null, polarity: null, silences: 0 });
      const polarity = transcript ? detectYesNo(alternatives) : null;
      logEvent('turn-summary', {
        turn: sttTurn, tier, card: card.id, stage: flow.stage, heard: transcript,
        alts: alternatives, source: sttSourceName, polarity, correct: !!polarity,
      });
      const finishLine = (path, text, uiMsg, endPhase = 'celebrate') => {
        g.flow = null;
        setRepeatTarget(null);
        setFeedbackText(uiMsg);
        updateGame({ phase: endPhase });
        say(path, text).then(() => { if (!gameOverRef.current) advanceToNextTier(tier, 200); });
      };
      const moveOnNoAnswer = () => {
        const another = packLine('letsTryAnother', '/audio/lets-try-another.mp3', "Let's try another one.");
        finishLine(another.audio, another.text, uiText('letsTryAnother', "Let's try another one."), 'giveAnswer');
      };

      // No usable answer on the BASE question → nudge (max 2), then move on.
      if (!polarity && flow.stage === null) {
        flow.silences += 1;
        if (flow.silences <= 2) {
          const dh = packLine('didntHear', '/audio/didnt-hear.mp3', "I didn't hear you. Try again!");
          setFeedbackText(uiText('didntHear', "I didn't hear you!"));
          updateGame({ phase: 'feedback' });
          say(dh.audio, dh.text).then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
          return;
        }
        moveOnNoAnswer();
        return;
      }

      // FEELINGS ("Do you like to learn?"): star either way, Frog reacts.
      if (tier === 'feelings') {
        const fl = ADVENTURE.feelings || {
          yes: { audio: '/audio/feelings-yay.mp3', text: 'Yay!' },
          no: { audio: '/audio/feelings-sorry.mp3', text: 'I am sorry to hear that.' },
        };
        setCorrectIds((prev) => new Set([...prev, `${card.id}-${tier}`]));
        if (polarity === 'yes') finishLine(fl.yes.audio, fl.yes.text, fl.yes.text, 'celebrate');
        else finishLine(fl.no.audio, fl.no.text, fl.no.text, 'giveAnswer'); // head-down frog, no fireworks
        return;
      }

      // YES/NO sentence ladder — waiting for the full-sentence echo.
      if (flow.stage === 'prompt' || flow.stage === 'final') {
        if (transcript && saidHaveSentence(alternatives)) {
          const gs = packLine('greatSentence', '/audio/great-sentence.mp3', 'Wonderful! You said the whole sentence!');
          finishLine(gs.audio, gs.text, uiText('wholeSentence', 'You said the whole sentence!'), 'celebrate');
          return;
        }
        if (flow.stage === 'prompt') {
          // Teach it: the game's only reveal — Frog speaks the sentence, asks
          // for the echo (same shape as the card-game final step).
          flow.stage = 'final';
          const saidYes = flow.polarity === 'yes';
          const rev = ADVENTURE.yesNoReveal(card, saidYes);
          setFeedbackText(uiText('repeatAfterMe', 'Repeat after me:'));
          setRepeatTarget(ADVENTURE.yesNoSentence(card, saidYes));
          updateGame({ phase: 'feedback' });
          speak(rev.audio, rev.text).then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
          return;
        }
        moveOnNoAnswer(); // failed the final echo — the star already earned stands
        return;
      }

      // BASE ANSWER: yes AND no both earn the star (script — the star is for
      // answering; the full sentence is what Frog models next).
      setCorrectIds((prev) => new Set([...prev, `${card.id}-${tier}`]));
      flow.polarity = polarity;
      if (saidHaveSentence(alternatives)) {
        const gs = packLine('greatSentence', '/audio/great-sentence.mp3', 'Wonderful! You said the whole sentence!');
        finishLine(gs.audio, gs.text, uiText('wholeSentence', 'You said the whole sentence!'), 'celebrate');
        return;
      }
      // Bare yes/no → prompt for the sentence (banner only — v4 rule: don't
      // give it away before the child has tried once). A "no" answer gets a
      // neutral "Okay!" instead of the excited "Yes!" — Frog must not sound
      // thrilled that a child dislikes vegetables (Natalya, 2026-08-18).
      flow.stage = 'prompt';
      const nfs = polarity === 'no'
        ? packLine('nowFullSentenceNo', '/audio/now-full-sentence-ok.mp3', 'Okay! Now say the whole sentence!')
        : packLine('nowFullSentence', '/audio/now-full-sentence.mp3', 'Yes! Now say the whole sentence!');
      setFeedbackText(uiText('nowFullSentence', 'Now say the whole sentence!'));
      setRepeatTarget(ADVENTURE.yesNoSentence(card, polarity === 'yes'));
      updateGame({ phase: 'feedback' });
      say(nfs.audio, nfs.text).then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
      return;
    }

    // ── LIST tier (Garden "Can you name the colors in the rainbow?",
    // 2026-08-18) — fully local, shared by AI and non-AI modes: the answer is
    // a list of KNOWN words, so counting matches beats any judge. Correct =
    // ≥ card.listMin distinct card.listWords heard (word-level match with the
    // usual lev≤1 tolerance, NO substring rule — 'red' must not fire inside
    // 'colored'). card.listSynonyms lets 'purple' count as violet. Flow
    // mirrors the card game: miss 1 → warm retry; miss 2 → the reveal ladder
    // (Frog says the list, child echoes); the echo still earns the star.
    if (gameRef.current.questionTier === 'list') {
      const g = gameRef.current;
      const card = deck[g.exerciseIndex] || deck[0];
      const flow = g.flow || (g.flow = { stage: null, silences: 0 });
      const targets = card.listWords || [];
      const minHits = card.listMin || targets.length;
      const syns = card.listSynonyms || {};
      // Matches ACCUMULATE across turns of this question (2026-08-18,
      // Natalya's play test: the recognizer finalized mid-list and Frog cut
      // her off — a child who got 4 colors out can add the rest next turn).
      const heard = flow.heard || (flow.heard = new Set());
      for (const alt of alternatives) {
        for (const w of cleanSpoken(alt).split(/\s+/).filter(Boolean)) {
          for (const t of targets) {
            const accepts = [t, ...(syns[t] || [])];
            if (accepts.some((a) => w === a || (w.length >= a.length - 1 && levenshtein(w, a) <= 1))) heard.add(t);
          }
        }
      }
      const hit = heard.size >= minHits;
      logEvent('turn-summary', {
        turn: sttTurn, tier: 'list', card: card.id, stage: flow.stage, heard: transcript,
        alts: alternatives, source: sttSourceName, matched: [...heard], need: minHits, correct: hit,
      });
      const ps = packSentence(card, 'list');
      const finishList = (path, text, uiMsg, endPhase) => {
        g.flow = null;
        setRepeatTarget(null);
        setFeedbackText(uiMsg);
        updateGame({ phase: endPhase });
        say(path, text).then(() => { if (!gameOverRef.current) advanceToNextTier('list', 200); });
      };
      const celebrateList = (star) => {
        if (star) setCorrectIds((prev) => new Set([...prev, `${card.id}-list`]));
        const pc = packCorrect(card, 'list');
        finishList(pc.audio, pc.text, pc.text, 'celebrate');
      };
      const moveOnList = () => {
        const another = packLine('letsTryAnother', '/audio/lets-try-another.mp3', "Let's try another one.");
        finishList(another.audio, another.text, uiText('letsTryAnother', "Let's try another one."), 'giveAnswer');
      };

      // SILENCE (AI mode delivers empty transcripts): nudge, no attempt burned (max 2).
      if (!transcript) {
        if (flow.stage === 'final') { moveOnList(); return; } // silent at the last step — wrap up kindly
        flow.silences += 1;
        if (flow.silences <= 2) {
          const dh = packLine('didntHear', '/audio/didnt-hear.mp3', "I didn't hear you. Try again!");
          setFeedbackText(uiText('didntHear', "I didn't hear you!"));
          updateGame({ phase: 'feedback' });
          say(dh.audio, dh.text).then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
          return;
        }
        // 3rd silence falls through and counts as a miss.
      }

      // FINAL ECHO after the reveal — the star is still earnable (same rule
      // as the card game's STAR_ON_FINAL_ECHO).
      if (flow.stage === 'final') {
        if (hit) { celebrateList(STAR_ON_FINAL_ECHO); return; }
        moveOnList(); return; // failed the echo — no grinding
      }

      // BASE QUESTION
      if (hit) { celebrateList(true); return; }
      if (g.attemptNumber === 1) {
        const soClose = packLine('soClose', '/audio/so-close.mp3', 'Ooh, so close! Try again!');
        const tryAgain = packLine('tryAgain', '/audio/try-again.mp3', 'No, please try again.');
        const l = heard.size >= 2 ? soClose : tryAgain; // partial list → warmer nudge
        setFeedbackText(l.text);
        updateGame({ attemptNumber: 2, phase: 'feedback' });
        say(l.audio, l.text).then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
        return;
      }
      // Second miss → teach it: Frog says the whole list, asks for the echo.
      flow.stage = 'final';
      const pr = packReveal(card, 'list');
      setFeedbackText(uiText('repeatAfterMe', 'Repeat after me:'));
      setRepeatTarget({ sentence: ps.sentence, highlight: ps.highlight });
      updateGame({ phase: 'feedback' });
      speak(pr.audio, pr.text).then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
      return;
    }

    // ── AI mode (?ai=1): Gemini judges the answer and speaks dynamic Puck feedback.
    // Runs for name/color/count tiers; favorite stays on the original flow below.
    // The instant local path below is left completely untouched for the default app.
    if (AI_MODE && gameRef.current.questionTier !== 'favorite') {
      (async () => {
        const g = gameRef.current;
        const tier = g.questionTier;
        const animal = deck[g.exerciseIndex] || deck[0];
        // A language-pack card may carry its own spoken number word ("dos").
        const nw = animal.numberWord || NUMBER_WORDS[animal.count]?.[0] || String(animal.count);

        // ══ v4 ANSWER FLOW (2026-07-07, per Natalya 1:1 + Polina's diagram) ══
        // Rules: the goal is always the full sentence; the frog never gives the
        // answer away early; "repeat after me" exists ONCE, as the final step,
        // always with the entire sentence. Silence/off-topic don't burn attempts.

        // The target sentence ("These cows are black" phrasing per Natalya).
        // A language pack builds its own (farm-es: "Estas son vacas.").
        let sentence, highlight;
        const ps = packSentence(animal, tier);
        if (ps) {
          sentence = ps.sentence;
          highlight = ps.highlight;
        } else if (tier === 'name') {
          sentence = animal.count > 1 ? `These are ${animal.plural}.` : `This is a ${animal.word}.`;
          highlight = animal.count > 1 ? animal.plural : animal.word;
        } else if (tier === 'color') {
          sentence = animal.count > 1 ? `These ${animal.plural} are ${animal.color}.` : `This ${animal.word} is ${animal.color}.`;
          highlight = animal.color;
        } else {
          sentence = animal.count > 1 ? `There are ${nw} ${animal.plural}.` : `There is ${nw} ${animal.word}.`;
          highlight = nw;
        }
        const keyWord = tier === 'name' ? (animal.count > 1 ? animal.plural : animal.word)
          : tier === 'color' ? animal.color.split(/\s+and\s+/)[0] : nw;
        const keyOk = () => tier === 'name' ? fuzzyMatchAny(alternatives, nameTierWords(animal))
          : tier === 'color' ? animal.color.split(/\s+and\s+/).some((cw) => fuzzyMatch(alternatives, cw))
          : fuzzyMatchNumber(alternatives, animal.count);

        // Three-layer sentence check: two instant local rules, judge on ambiguity.
        const saidFullSentence = async () => {
          const words = cleanSpoken(transcript).split(/\s+/).filter(Boolean);
          const hasKey = keyOk();
          if (hasKey && words.length >= 3) return true; // "these are cows"
          const LINKERS = ADVENTURE.linkers || ['its', 'theyre', 'there', 'these', 'those', 'this', 'they', 'is', 'are', 'i', 'we', 'see'];
          if (hasKey && words.length === 2 && words.some((w) => LINKERS.includes(w.replace(/'/g, '')))) return true; // "it's geese"
          // Two CONTENT words that together answer the picture ("caballos
          // marrones", "brown horses", "two pigs") = a full answer, no follow-up
          // (2026-10-20, Natalya tracker #8). Key word + another fact about
          // the same card: a later tier it volunteers, or the card's name.
          if (hasKey && words.length === 2 && (volunteeredTiers(alternatives, animal, tier).length > 0
            || (tier !== 'name' && fuzzyMatchAny(alternatives, nameTierWords(animal))))) return true;
          if (words.length < 2) return false; // single word — no judge needed
          // Garbled key word or odd order → ask the judge (selective AI).
          setThinking(true);
          const tk = performance.now();
          try {
            const r = await fetch(`${API_BASE}/api/evaluate-sentence`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ spokenText: transcript, alternatives, expectedSentence: sentence, targetWord: keyWord,
                targetLanguage: TARGET_LANGUAGE }),
            }).then((x) => x.json());
            aiTimings.think = Math.round(performance.now() - tk);
            return Boolean(r && r.saidSentence);
          } catch (e) { logEvent('sentence-judge-FAIL', e.message); return false; }
          finally { setThinking(false); }
        };

        // ── Compound-answer credit (2026-08-03) ──
        // Star + skip any LATER tier this answer already covered. Returns the
        // list of tiers credited from THIS utterance (empty when none).
        const creditVolunteered = () => {
          const set = g.creditedTiers || (g.creditedTiers = new Set());
          // Only NEWLY covered tiers count — a repeat of something already
          // credited earlier in the ladder isn't a fresh bonus.
          const fresh = volunteeredTiers(alternatives, animal, tier).filter((t) => !set.has(t));
          if (fresh.length) {
            fresh.forEach((t) => set.add(t));
            setCorrectIds((prev) => new Set([...prev, ...fresh.map((t) => `${animal.id}-${t}`)]));
            logEvent('bonus-credit', { turn: sttTurn, card: animal.id, tier, credited: fresh, heard: transcript });
          }
          return fresh;
        };
        // Tiers still genuinely left on this card after credits are applied.
        const remainingAfterCredits = () => {
          const tiers = animal.tiers || ['name', 'color', ...(animal.count > 1 ? ['count'] : [])];
          return tiers.slice(tiers.indexOf(tier) + 1)
            .filter((t) => !(g.creditedTiers && g.creditedTiers.has(t)));
        };
        // Celebration line: acknowledge how much was covered instead of the
        // generic sentence praise ("I just told you pink!" fix).
        const bonusLine = (extra) => {
          if (!extra.length) return null;
          if (remainingAfterCredits().length === 0) {
            const l = packLine('bonusAll', '/audio/bonus-all.mp3', 'Incredible! You told me everything about this picture! You get all the stars!');
            return [l.audio, l.text, uiText('bonusAllShort', 'You told me everything!')];
          }
          const l = packLine('bonusExtra', '/audio/bonus-extra.mp3', 'Wow! You told me even more than I asked! Bonus star!');
          return [l.audio, l.text, uiText('bonusExtraShort', 'Bonus star!')];
        };

        // ── Shared outcomes ──
        const celebrate = (withStar) => {
          g.flow = null;
          if (withStar) setCorrectIds((prev) => new Set([...prev, `${animal.id}-${tier}`]));
          const bonus = bonusLine(creditVolunteered());
          const great = packLine('greatSentence', '/audio/great-sentence.mp3', 'Wonderful! You said the whole sentence!');
          setFeedbackText(bonus ? bonus[2] : uiText('wholeSentence', 'You said the whole sentence!'));
          setRepeatTarget(null);
          updateGame({ phase: 'celebrate' });
          say(bonus ? bonus[0] : great.audio,
            bonus ? bonus[1] : great.text)
            .then(() => { if (!gameOverRef.current) advanceToNextTier(tier, 200); });
        };
        const moveOnNoStar = () => {
          g.flow = null;
          setRepeatTarget(null);
          const another = packLine('letsTryAnother', '/audio/lets-try-another.mp3', "Let's try another one.");
          setFeedbackText(uiText('letsTryAnother', "Let's try another one."));
          updateGame({ phase: 'giveAnswer' });
          say(another.audio, another.text)
            .then(() => { if (!gameOverRef.current) advanceToNextTier(tier, 200); });
        };
        // FINAL STEP — the game's only "repeat after me", always the full sentence.
        const goFinalStep = () => {
          g.flow.stage = 'final';
          setSttContext(ADVENTURE.sttSentenceContext || 'a short spoken sentence about the picture');
          const pr = packReveal(animal, tier);
          const revealPath = pr ? pr.audio
            : tier === 'name' ? `/audio/${animal.id}-reveal.mp3`
            : tier === 'color' ? `/audio/${animal.id}-color-reveal.mp3`
            : `/audio/${animal.id}-count-reveal.mp3`;
          setFeedbackText(uiText('repeatAfterMe', 'Repeat after me:'));
          setRepeatTarget({ sentence, highlight });
          updateGame({ phase: 'feedback' });
          speak(revealPath, pr ? pr.text : `${sentence} Repeat after me. ${sentence}`)
            .then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
        };

        const flow = g.flow || (g.flow = { stage: null, englishPrompted: false, offTopics: 0, silences: 0 });

        // ── SILENCE: heard nothing → gentle nudge, NO attempt used (max 2) ──
        if (!transcript) {
          flow.silences += 1;
          logEvent('turn-silence', { turn: sttTurn, tier, stage: flow.stage, count: flow.silences });
          if (flow.stage === 'final') { moveOnNoStar(); return; } // silent at the last step — wrap up kindly
          if (flow.silences <= 2) {
            const dh = packLine('didntHear', '/audio/didnt-hear.mp3', "I didn't hear you. Try again!");
            setFeedbackText(uiText('didntHear', "I didn't hear you!"));
            updateGame({ phase: 'feedback' });
            say(dh.audio, dh.text)
              .then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
            return;
          }
          if (flow.stage === 'prompt') { goFinalStep(); return; }
          // 3rd+ silence on the base question falls through and counts as wrong.
        }

        // ── SENTENCE LADDER: we're waiting for the full sentence ──
        if (flow.stage === 'prompt' || flow.stage === 'final') {
          const ok = transcript ? await saidFullSentence() : false;
          aiTimings.total = Math.round(performance.now() - aiTimings.turnStart);
          logEvent('turn-summary', {
            turn: sttTurn, tier: `${tier}-sentence`, card: animal?.id, stage: flow.stage,
            expected: sentence, heard: transcript, alts: alternatives, source: sttSourceName,
            correct: ok, ms: { record: aiTimings.record, stt: aiTimings.stt, think: aiTimings.think },
          });
          if (ok) { celebrate(flow.stage === 'prompt' || STAR_ON_FINAL_ECHO); return; }
          if (flow.stage === 'prompt') { goFinalStep(); return; } // didn't manage it → teach it
          moveOnNoStar(); return; // failed the final echo — no grinding
        }

        // ── BASE QUESTION: judge the first-level answer ──
        let correct = keyOk();
        let otherLang = false, wasClose = false, offTopic = false;
        if (!correct && transcript) {
          setThinking(true);
          const tk = performance.now();
          try {
            const r = await fetch(`${API_BASE}/api/evaluate`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ spokenText: transcript, alternatives, targetWord: keyWord, attemptNumber: g.attemptNumber,
                // Adventure-aware judging (2026-08-11): without these the judge
                // prompt assumed Farm and missed native-language Playground
                // answers (Natalya's Russian test → no "say it in English").
                // targetLanguage (2026-08-12): Spanish packs grade Spanish.
                topic: ADVENTURE.judgeTopic, translationHints: ADVENTURE.judgeHints,
                targetLanguage: TARGET_LANGUAGE }),
            }).then((x) => x.json());
            if (r) { correct = !!r.correct; otherLang = !!r.inOtherLanguage; wasClose = !!r.close; offTopic = !!r.offTopic; }
          } catch (e) { logEvent('ai-evaluate-FAIL', e.message); }
          finally { setThinking(false); }
          aiTimings.think = Math.round(performance.now() - tk);
          logEvent('ai-timing', { phase: 'think-rescue', think: aiTimings.think });
        }
        aiTimings.total = Math.round(performance.now() - aiTimings.turnStart);
        logEvent('turn-summary', {
          turn: sttTurn, tier, card: animal?.id,
          expected: tier === 'name' ? animal.plural : tier === 'color' ? animal.color : animal.count,
          heard: transcript, alts: alternatives, source: sttSourceName,
          correct, otherLang, offTopic, close: wasClose,
          rescued: correct && aiTimings.think > 0, attempt: g.attemptNumber,
          ms: { record: aiTimings.record, stt: aiTimings.stt, think: aiTimings.think, total: aiTimings.total },
        });

        // OFF-TOPIC (talking to mom, narrating): re-ask the question, NO attempt (max 2).
        if (offTopic && flow.offTopics < 2) {
          flow.offTopics += 1;
          const pq2 = packQ(animal, tier);
          const nameAsk = animal.ask || (animal.count > 1
            ? { audio: '/audio/what-are-these.mp3', text: 'What are these?' }
            : { audio: '/audio/what-is-this.mp3', text: 'What is this?' });
          const askPath = pq2 ? pq2.audio
            : tier === 'name' ? nameAsk.audio
            : tier === 'color' ? `/audio/${animal.id}-color-ask.mp3`
            : `/audio/${animal.id}-count-ask.mp3`;
          const askText = pq2 ? pq2.text
            : tier === 'name' ? nameAsk.text
            : tier === 'color' ? `What color ${animal.count > 1 ? 'are' : 'is'} the ${animal.count > 1 ? animal.plural : animal.word}?`
            : `How many ${animal.plural} do you see?`;
          setFeedbackText('');
          updateGame({ phase: 'feedback' });
          speak(askPath, askText).then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
          return;
        }

        // CORRECT — full sentence right away wins instantly; a lone word starts the ladder.
        if (correct) {
          if (await saidFullSentence()) {
            g.flow = null;
            const bonus = bonusLine(creditVolunteered());
            const pc = packCorrect(animal, tier);
            let path, msg;
            if (bonus) { [path, msg] = bonus; }
            else if (pc) { path = pc.audio; msg = pc.text; }
            else if (tier === 'name') { path = `/audio/${animal.id}-correct.mp3`; msg = animal.count > 1 ? `Yes! These are ${animal.plural}.` : `Yes! This is a ${animal.word}.`; }
            else if (tier === 'color') { path = `/audio/${animal.id}-color-correct.mp3`; msg = animal.count > 1 ? `Yes! These are ${animal.color} ${animal.plural}.` : `Yes! It is a ${animal.color} ${animal.word}.`; }
            else { path = `/audio/${animal.id}-count-correct.mp3`; msg = animal.count > 1 ? `Yes! There are ${nw} ${animal.plural}.` : `Yes! There is ${nw} ${animal.word}.`; }
            setCorrectIds((prev) => new Set([...prev, `${animal.id}-${tier}`]));
            setFeedbackText(bonus ? bonus[2] : msg);
            setRepeatTarget(null);
            updateGame({ phase: 'celebrate' });
            (bonus ? say(path, msg) : speak(path, msg))
              .then(() => { if (!gameOverRef.current) advanceToNextTier(tier, 200); });
            return;
          }
          // Right word, but not a sentence yet — still bank anything extra the
          // child volunteered ("two pigs" has already answered the count).
          creditVolunteered();
          // Right word, one word: "Now say the whole sentence!" — banner shows it,
          // the frog does NOT say it (v4 rule: never give it away early).
          flow.stage = 'prompt';
          setSttContext(ADVENTURE.sttSentenceContext || 'a short spoken sentence about the picture');
          const nfs = packLine('nowFullSentence', '/audio/now-full-sentence.mp3', 'Yes! Now say the whole sentence!');
          setFeedbackText(uiText('nowFullSentence', 'Now say the whole sentence!'));
          setRepeatTarget({ sentence, highlight });
          updateGame({ phase: 'feedback' });
          say(nfs.audio, nfs.text)
            .then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
          return;
        }

        // NATIVE LANGUAGE, right meaning: "Say it in English!" — WITHOUT the word
        // (v4 rule). Second native answer → the final teaching step.
        if (otherLang) {
          if (!flow.englishPrompted) {
            flow.englishPrompted = true;
            const sit = packLine('sayInTarget', '/audio/say-in-english.mp3', "That's right — but let's say it in English!");
            setFeedbackText(uiText('sayInTarget', "Let's say it in English!"));
            setRepeatTarget(null);
            updateGame({ phase: 'feedback' });
            say(sit.audio, sit.text)
              .then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
            return;
          }
          goFinalStep();
          return;
        }

        // WRONG — first miss gets a warm retry; second miss goes to the final step.
        if (g.attemptNumber === 1) {
          const soClose = packLine('soClose', '/audio/so-close.mp3', 'Ooh, so close! Try again!');
          const tryAgain = packLine('tryAgain', '/audio/try-again.mp3', 'No, please try again.');
          const notQuite = packLine('notQuite', '/audio/not-quite.mp3', 'Hmm, not quite — one more try!');
          let path, msg;
          if (wasClose) { path = soClose.audio; msg = soClose.text; }
          else if (Math.random() < 0.5) { path = tryAgain.audio; msg = tryAgain.text; }
          else { path = notQuite.audio; msg = notQuite.text; }
          setFeedbackText(msg);
          updateGame({ attemptNumber: 2, phase: 'feedback' });
          say(path, msg).then(() => { if (!gameOverRef.current) updateGame({ phase: 'respond' }); });
          return;
        }
        goFinalStep();
      })();
      return;
    }

    const g = gameRef.current;
    const tier = g.questionTier;
    const animal = deck[g.exerciseIndex] || deck[0];
    console.log(`[PROCESS] tier=${tier}, transcript="${transcript}", attempt=${g.attemptNumber}`);
    logEvent('answer', { tier, heard: transcript, attempt: g.attemptNumber, card: animal?.id });

    // ── FAVORITE (open-ended, 3-attempt limit) ──
    if (tier === 'favorite') {
      // 1) Right answer → celebrate and complete, regardless of attempt #
      // Local resolve: pool word → synonym → curated extras (see
      // matchFavoriteAnimal). Anything else falls to the Gemini judge below.
      const matchedAnimal = matchFavoriteAnimal(alternatives);
      logEvent('turn-summary', {
        turn: sttTurn, tier: 'favorite', heard: transcript, alts: alternatives,
        source: sttSourceName, matched: matchedAnimal ? matchedAnimal.word : null,
        generic: !!(matchedAnimal && matchedAnimal.generic),
        correct: !!matchedAnimal,
      });
      // Shared outcomes for both the instant local path and the AI judge path.
      // ALWAYS name the animal the child chose (Natalya 2026-07-16: she heard
      // the generic "I love that animal too" for quail and wants "I love quails
      // too"). fav.generic now means only "no pre-baked audio file exists" —
      // NOT "don't name it". The 9 core pool animals (+ synonym-mapped ones)
      // have cached Puck WAVs and play instantly; every other accepted animal
      // is spoken with a live Puck line via /api/speak (generated once, then
      // cached server-side in S3, so it's fast and free on replay).
      const celebrateFavorite = (fav) => {
        const FC = favConf();
        // Garden (2026-08-18): its favorite IS question 10 of the script, so a
        // real answer earns the 10th star (Farm's finale stays an unstarred
        // bonus — earnsStar is opt-in per pack).
        if (FC?.earnsStar) setCorrectIds((prev) => new Set([...prev, 'favorite']));
        // A language pack builds its own celebration ("¡A mí también me
        // encantan las vacas!") and audio path (es-favorite-<word>).
        const msg = FC?.celebrateLine ? FC.celebrateLine(fav) : `I love ${fav.plural} too! Great choice.`;
        setFeedbackText(msg);
        updateGame({ phase: 'celebrate' });
        // NOTE: no gameOverRef guard here — the favorite flow runs intentionally
        // AFTER gameOverRef is set to true, so guarding would silently block
        // the transition to the complete screen.
        const toComplete = () => updateGame({ phase: 'complete' });
        if (fav.generic) {
          // No cached file for this animal → synthesize the named line live so
          // the frog still says the animal by name (no /audio/favorite-generic).
          speakText(msg).then(toComplete);
        } else {
          const FC2 = favConf();
          speak(FC2?.audioFor ? FC2.audioFor(fav.word) : `/audio/favorite-${fav.word}.mp3`, msg).then(toComplete);
        }
      };
      const favoriteMiss = (isKnownAnimal) => {
        // Unclear answer (judge recognized nothing) on a pack that opts in
        // (Playground, 2026-10-20): Frog shares his own favorite and the
        // session wraps up — no "not a toy" verdict on garbled speech.
        const UC = favConf()?.unclear;
        if (UC && !isKnownAnimal) {
          logEvent('favorite-unclear', { attempt: favoriteAttemptsRef.current + 1 });
          setFeedbackText(UC.line);
          updateGame({ phase: 'celebrate' });
          speak(UC.audio, UC.line).then(() => updateGame({ phase: 'complete' }));
          return;
        }
        favoriteAttemptsRef.current += 1;
        const attempt = favoriteAttemptsRef.current;
        if (attempt === 1) {
          // Pack-specific rejection lines (2026-08-11). Farm distinguishes
          // known-animal from not-an-animal; Playground uses one "not a toy"
          // line for both. Fallbacks preserve the original Farm wording.
          const FC = favConf();
          const miss = isKnownAnimal
            ? (FC?.missKnown || { line: "That's not a farm animal.", audio: '/audio/favorite-not-farm.mp3' })
            : (FC?.missUnknown || { line: "That's not an animal.", audio: '/audio/favorite-not-animal.mp3' });
          const msg = miss.noReask ? miss.line : `${miss.line} ${favQuestion()}`;
          setFeedbackText(msg);
          speak(miss.audio, msg);
        } else if (attempt === 2) {
          const hint = favConf()?.hint || { line: "Let's pick together. How about a cow or a horse?", audio: '/audio/favorite-hint.mp3' };
          setFeedbackText(hint.line);
          speak(hint.audio, hint.line);
        } else {
          const wrap = packLine('favoriteWrapup', '/audio/favorite-wrapup.mp3', "That's okay. Let's keep exploring!");
          setFeedbackText(wrap.text);
          updateGame({ phase: 'celebrate' });
          speak(wrap.audio, wrap.text).then(() => {
            updateGame({ phase: 'complete' });
          });
        }
      };

      if (matchedAnimal) { celebrateFavorite(matchedAnimal); return; }

      // AI mode (2026-07-08): the child may name their favorite in ANY language
      // (el pollo, корова, caballo). Judge maps it to the English pool word:
      // other language → "say it in English" once, then accept warmly (this is
      // the open-ended finale — no reveal exists, so no grinding). No attempt
      // is burned on a right-animal-wrong-language answer.
      if (AI_MODE) {
        (async () => {
          setThinking(true);
          let r = null;
          const tk = performance.now();
          try {
            r = await fetch(`${API_BASE}/api/evaluate-favorite`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ spokenText: transcript, alternatives, options: getAllAnimalWords(),
                kind: favConf()?.judgeKind || 'farm animal',
                // Language packs (farm-es): judge maps to the pack's language.
                targetLanguage: favConf()?.language || TARGET_LANGUAGE }),
            }).then((x) => x.json());
          } catch (e) { logEvent('favorite-judge-FAIL', e.message); }
          finally { setThinking(false); }
          aiTimings.think = Math.round(performance.now() - tk);
          logEvent('favorite-judge', { turn: sttTurn, heard: transcript, farmAnimal: r?.farmAnimal, otherLang: r?.inOtherLanguage, knownAnimal: r?.knownAnimal, judgeError: (!r || r.error) || undefined });
          // Judge unreachable or errored (network blip, quota, cold start):
          // NEVER speak an API failure as a confident "That's not an animal"
          // (2026-08-11 — Natalya's rabbit got exactly that). Re-ask once
          // without burning an attempt; if it fails again, go to the friendly
          // pick-together hint instead of a classification.
          if (!r || r.error) {
            favoriteJudgeFailsRef.current += 1;
            if (favoriteJudgeFailsRef.current <= 1) {
              const dh = packLine('didntHear', '/audio/didnt-hear.mp3', "I didn't hear you. Try again!");
              const msg = `${uiText('didntHear', "I didn't hear you.")} ${favQuestion()}`;
              setFeedbackText(msg);
              say(dh.audio, dh.text);
              return;
            }
            favoriteAttemptsRef.current = Math.max(favoriteAttemptsRef.current, 1);
            favoriteMiss(true); // lands on "Let's pick together..." — never a verdict
            return;
          }
          // The judge is unshackled (2026-07-12): farmAnimal may be ANY English
          // farm-animal word, not just a pool option. Pool word → its own audio;
          // synonym → mapped pool animal; anything else → generic celebration.
          // These judge approvals surface in diag logs (favorite-judge events)
          // for periodic review + promotion into FAVORITE_EXTRAS.
          let fav = null;
          if (r && r.farmAnimal) {
            const w = String(r.farmAnimal).toLowerCase().trim();
            const allGeneric = !!favConf()?.allGeneric;
            const syns = favSynonyms();
            const poolFav = favoritePool().find((a) => a.word === w || a.plural === w);
            const synFav = syns[w] ? favoritePool().find((a) => a.word === syns[w]) : null;
            fav = poolFav ? { word: poolFav.word, plural: poolFav.plural, generic: allGeneric }
              : synFav ? { word: synFav.word, plural: synFav.plural, generic: allGeneric }
              : { word: w, plural: w, generic: true };
          }
          if (fav && r.inOtherLanguage && !favoriteEnglishPromptedRef.current) {
            favoriteEnglishPromptedRef.current = true;
            const sit = packLine('sayInTarget', '/audio/say-in-english.mp3', "That's right — but let's say it in English!");
            setFeedbackText(uiText('sayInTarget', "Let's say it in English!"));
            say(sit.audio, sit.text);
            return;
          }
          if (fav) { celebrateFavorite(fav); return; }
          favoriteMiss(r ? r.knownAnimal : COMMON_ANIMALS.some((w) => fuzzyMatch(alternatives, w)));
        })();
        return;
      }

      favoriteMiss(COMMON_ANIMALS.some((w) => fuzzyMatch(alternatives, w)));
      return;
    }

    // ── Determine correct answer based on tier ──
    let isCorrect = false;
    if (tier === 'name') {
      // Accept both singular and plural — kids naturally say "geese", "rabbits",
      // "pigs" when looking at a multi-animal card. Both forms count as correct.
      // Plus known STT sound-alikes for this target (ducks→dogs, 2026-07-12).
      isCorrect = fuzzyMatchAny(alternatives, nameTierWords(animal));
    } else if (tier === 'color') {
      // For multi-word colors like "black and white", accept any component word
      const colorWords = animal.color.split(/\s+and\s+/);
      isCorrect = colorWords.some((cw) => fuzzyMatch(alternatives, cw));
    } else if (tier === 'count') {
      isCorrect = fuzzyMatchNumber(alternatives, animal.count);
    }

    logEvent('turn-summary', {
      turn: sttTurn, tier, card: animal?.id,
      expected: tier === 'name' ? animal.plural : tier === 'color' ? animal.color : animal.count,
      heard: transcript, alts: alternatives, source: sttSourceName,
      correct: isCorrect, attempt: g.attemptNumber,
      ms: { record: aiTimings.record, stt: aiTimings.stt, total: aiTimings.total },
    });

    // ── CORRECT ──
    if (isCorrect) {
      const pc = packCorrect(animal, tier);
      let audioPath, msg;
      if (pc) {
        audioPath = pc.audio;
        msg = pc.text;
      } else if (tier === 'name') {
        audioPath = `/audio/${animal.id}-correct.mp3`;
        msg = animal.count > 1
          ? `Yes! These are ${animal.plural}.`
          : `Yes! This is a ${animal.word}.`;
      } else if (tier === 'color') {
        audioPath = `/audio/${animal.id}-color-correct.mp3`;
        msg = animal.count > 1
          ? `Yes! These are ${animal.color} ${animal.plural}.`
          : `Yes! It is a ${animal.color} ${animal.word}.`;
      } else {
        audioPath = `/audio/${animal.id}-count-correct.mp3`;
        const numWord = animal.numberWord || NUMBER_WORDS[animal.count]?.[0] || String(animal.count);
        msg = animal.count > 1
          ? `Yes! There are ${numWord} ${animal.plural}.`
          : `Yes! There is ${numWord} ${animal.word}.`;
      }

      setFeedbackText(msg);
      setRepeatTarget(null);
      if (g.attemptNumber === 1) {
        setCorrectIds((prev) => new Set([...prev, `${animal.id}-${tier}`]));
      }
      updateGame({ phase: 'celebrate' });

      // Wait for audio to actually finish before advancing — a hard-coded 2500ms
      // timer was cutting off longer phrases like "Yes! These are brown horses."
      // The 600ms pause after audio ends gives a natural beat between sentences.
      // Every card gets all 3 tiers (name → color → count), regardless of attempt
      // number. After the count tier, advanceToNextTier falls through to advanceToNextCard.
      speak(audioPath, msg).then(() => {
        if (!gameOverRef.current) advanceToNextTier(tier, 200);
      });
      return;
    }

    // ── WRONG — max attempts reached → move on to next tier on the same card ──
    if (g.attemptNumber >= MAX_ATTEMPTS) {
      const another = packLine('letsTryAnother', '/audio/lets-try-another.mp3', "Let's try another one.");
      setFeedbackText(uiText('letsTryAnother', "Let's try another one."));
      setRepeatTarget(null);
      updateGame({ phase: 'giveAnswer' });
      // Wait for audio to finish before advancing (was 3500ms fixed timer).
      speak(another.audio, another.text).then(() => {
        if (!gameOverRef.current) advanceToNextTier(tier, 200);
      });
      return;
    }

    // ── WRONG — attempt 1 → try again ──
    if (g.attemptNumber === 1) {
      const tryAgain = packLine('tryAgain', '/audio/try-again.mp3', 'No, please try again.');
      setFeedbackText(tryAgain.text);
      updateGame({ attemptNumber: 2, phase: 'feedback' });
      speak(tryAgain.audio, tryAgain.text).then(() => {
        if (!gameOverRef.current) updateGame({ phase: 'respond' });
      });
      return;
    }

    // ── WRONG — attempt 2 → reveal answer ──
    // Per teacher feedback: kids should repeat the FULL sentence, not just
    // the key word. So "Say it with me... pig" becomes "Repeat after me.
    // These are pigs." Color repeat uses the form "{Plural} are {color}."
    // (e.g. "Pigs are pink. Cows are black.") and count uses "There are
    // {n} {plural}." — natural, complete sentences a child can echo back.
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    // Each reveal = full audio sentence + a separate on-screen "echo target":
    // the exact sentence the child must repeat, shown BIG under the card with
    // the key vocabulary word highlighted. The frog's bubble shows only
    // "Repeat after me:" — the banner carries the content.
    let revealPath, revealMsg, echoSentence, echoWord;
    const prv = packReveal(animal, tier);
    const psv = packSentence(animal, tier);
    if (prv && psv) {
      revealPath = prv.audio;
      revealMsg = prv.text;
      echoSentence = psv.sentence;
      echoWord = psv.highlight;
    } else if (tier === 'name') {
      revealPath = `/audio/${animal.id}-reveal.mp3`;
      if (animal.count > 1) {
        echoSentence = `These are ${animal.plural}.`;
        echoWord = animal.plural;
      } else {
        echoSentence = `This is a ${animal.word}.`;
        echoWord = animal.word;
      }
      revealMsg = `${echoSentence} Repeat after me. ${echoSentence}`;
    } else if (tier === 'color') {
      // "These cows are black" phrasing (Natalya 2026-07-07: "cows are black"
      // overgeneralizes to all cows). Matches the regenerated reveal audio.
      revealPath = `/audio/${animal.id}-color-reveal.mp3`;
      if (animal.count > 1) {
        echoSentence = `These ${animal.plural} are ${animal.color}.`;
        revealMsg = `${echoSentence} Repeat after me. ${echoSentence}`;
      } else {
        echoSentence = `This ${animal.word} is ${animal.color}.`;
        revealMsg = `${echoSentence} Repeat after me. ${echoSentence}`;
      }
      echoWord = animal.color;
    } else {
      const numWord = NUMBER_WORDS[animal.count]?.[0] || String(animal.count);
      revealPath = `/audio/${animal.id}-count-reveal.mp3`;
      echoSentence = animal.count > 1
        ? `There are ${numWord} ${animal.plural}.`
        : `There is ${numWord} ${animal.word}.`;
      echoWord = numWord;
      revealMsg = `${echoSentence} Repeat after me. ${echoSentence}`;
    }

    setFeedbackText(uiText('repeatAfterMe', 'Repeat after me:'));
    setRepeatTarget({ sentence: echoSentence, highlight: echoWord });
    updateGame({ attemptNumber: g.attemptNumber + 1, phase: 'feedback' });
    speak(revealPath, revealMsg).then(() => {
      if (!gameOverRef.current) updateGame({ phase: 'respond' });
    });
  }, [transcriptId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Mic toggle ───
  const handleMic = () => {
    // Every mic tap is a real user gesture — the only thing iOS reliably
    // accepts for re-unlocking audio after it suspends the session mid-game
    // (frog went mute on iPhone even with the programmatic resume, 2026-07-03).
    unlockAudio();
    if (listening) { stopListening(); }
    else { stopAudio(); startListening(); }
  };

  // ─── Restart ───
  const handleRestart = () => {
    cancelTimers();
    gameOverRef.current = false;
    completionPlayedRef.current = false;
    lastProcessedTranscriptId.current = 0;
    favoriteAttemptsRef.current = 0;
    favoriteEnglishPromptedRef.current = false;
    favoriteJudgeFailsRef.current = 0;
    const newDeck = buildDeck();
    setDeck(newDeck);
    Object.assign(gameRef.current, { exerciseIndex: 0, attemptNumber: 1, phase: 'intro', questionTier: 'name', flow: null });
    setExerciseIndex(0);
    setCorrectIds(new Set());
    setPhase('intro');
    setFeedbackText('');
    setRepeatTarget(null);
    setAttemptNumber(1);
    setQuestionTier('name');
  };

  // ─── Exit — stop everything and leave the session ───
  // (window.close() does nothing for a tab the user opened normally, so the
  // button appeared dead. Resets the game and shows the start screen.)
  const resetToStart = () => {
    gameOverRef.current = false;
    completionPlayedRef.current = false;
    lastProcessedTranscriptId.current = 0;
    gameRef.current.childName = ''; // a different child may be next (opener re-runs from Start)
    favoriteAttemptsRef.current = 0;
    favoriteEnglishPromptedRef.current = false;
    favoriteJudgeFailsRef.current = 0;
    setDeck(buildDeck());
    Object.assign(gameRef.current, { exerciseIndex: 0, attemptNumber: 1, phase: 'start', questionTier: 'name', flow: null });
    setExerciseIndex(0);
    setCorrectIds(new Set());
    setPhase('start');
    setFeedbackText('');
    setRepeatTarget(null);
    setAttemptNumber(1);
    setQuestionTier('name');
  };
  const handleExit = () => {
    cancelTimers();
    stopAudio();
    if (listening) stopListening();
    // With the navigation screen live (2026-08-07), Exit returns to the
    // language/adventure picker.
    if (!WP_LINK.token && !WP_LINK.lessonId && !WP_LINK.userId) {
      const q = new URLSearchParams(window.location.search);
      const lang = q.get('lang') || 'en';
      window.location.search = `lang=${lang}`;
      return;
    }
    // Auto-record (tracker #1): never leave with the recording unsent — stop
    // and send what was recorded (or let an in-flight upload finish), with a
    // "Saving…" screen, then leave. Capped so a dead network can't trap a kid.
    if (exitSavingRef.current === 'saving') return; // double-tap while saving
    if (AUTO_RECORD && exitSavingRef.current !== 'done') {
      const busy = sessionRec.status === 'recording'
        || sessionRec.uploadStatus === 'converting' || sessionRec.uploadStatus === 'sending';
      if (busy) {
        exitSavingRef.current = 'saving';
        sessionRec.exitSaving = true;
        const waitUpload = async () => {
          const t0 = Date.now();
          while ((sessionRec.uploadStatus === 'converting' || sessionRec.uploadStatus === 'sending') && Date.now() - t0 < 15000) {
            await new Promise((r) => setTimeout(r, 200));
          }
        };
        (sessionRec.status === 'recording' ? finishAutoRecording(score, 'exit', 15000) : waitUpload())
          .catch(() => {})
          .then(() => { sessionRec.exitSaving = false; exitSavingRef.current = 'done'; handleExit(); });
        return;
      }
    }
    exitSavingRef.current = false;
    // WordPress lesson link (2026-08-12, Polina): ✕ should land the student
    // back on the Farm Adventure lesson page — matching every other activity
    // there — not on this app's start screen. The WP tile navigates in the
    // same tab, so the lesson page is one step back in history. If back can't
    // navigate (link opened directly or in a fresh tab), the timeout falls
    // through to the old reset-to-start behavior instead.
    if (window.history.length > 1) {
      logEvent('wp-exit-back', { historyLen: window.history.length });
      window.history.back();
      setTimeout(resetToStart, 800);
      return;
    }
    logEvent('wp-exit-no-history', {});
    resetToStart();
  };

  // ════════════════════════════════════════════════════
  // ─── START SCREEN ──────────────────────────────────
  // ════════════════════════════════════════════════════
  if (phase === 'start') {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Nunito', sans-serif", position: 'relative', cursor: 'pointer' }} onClick={handleStart}>
        <FarmBackground />
        <RotateHint />
        <SessionRecorderButton score={score} />
        <WpLinkGate />
        <div style={{ position: 'fixed', top: 14, left: 20, zIndex: 10 }}><LittleSpongesLogo /></div>
        {/* ✕ / 🏠 on the start screen too (2026-10-20, Natalya #3): a child who
            lands here by mistake must be able to leave without starting a
            session — same button and behavior as inside the adventure.
            stopPropagation: the whole screen is a tap-to-start target. */}
        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          onClick={(e) => { e.stopPropagation(); handleExit(); }}
          aria-label={IS_WP_LINK ? 'Exit' : 'Menu'}
          style={{ position: 'fixed', top: 12, right: 12, width: 36, height: 36, borderRadius: '50%',
            border: `3px solid ${IS_WP_LINK ? BRAND.redDark : BRAND.teal}`, background: IS_WP_LINK ? BRAND.red : BRAND.tealLight, color: BRAND.white,
            fontSize: 18, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', boxShadow: '0 3px 10px rgba(0,0,0,0.2)', zIndex: 20 }}>{IS_WP_LINK ? '✕' : '🏠'}</motion.button>
        <Scaled scale={uiScale}>
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, zIndex: 1 }}>
            <FrogAvatar size={500} state={speaking ? 'talking' : 'idle'} />
            <motion.div animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 1.5 }}
              style={{ background: `radial-gradient(circle at 35% 35%, ${ACCENT}, ${ACCENT_DARK})`,
                borderRadius: 30, padding: '20px 50px', border: `4px solid ${ACCENT_DARK}`,
                boxShadow: `0 8px 24px ${ACCENT_DARK}60` }}>
              <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: 36, fontWeight: 900, color: BRAND.white }}>
                {uiText('tapToStart', 'Tap to Start!')}
              </span>
            </motion.div>
          </motion.div>
        </Scaled>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  // ─── OPENER SCREEN — "What is your name?" (School) ─
  // ════════════════════════════════════════════════════
  // No picture card: this is Frog's personal question, so Frog takes center
  // stage with the mic below — same interaction pattern as every other turn.
  if (phase === 'opener' && ADVENTURE.opener) {
    return (
      <div style={{ height: '100vh', overflow: 'hidden', fontFamily: "'Nunito', sans-serif",
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <FarmBackground />
        <RotateHint />
        <SessionRecorderButton score={score} />
        <WpLinkGate />
        <SttDebugOverlay transcript={transcript} alternatives={alternatives} listening={listening} />
        <div style={{ position: 'fixed', top: 14, left: 20, zIndex: 10 }}><LittleSpongesLogo /></div>
        <Scaled scale={uiScale}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, zIndex: 1 }}>
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              style={{ fontSize: 30, fontWeight: 900, color: BRAND.white,
                textShadow: `2px 2px 6px ${BRAND.teal}`, textAlign: 'center' }}>
              {ADVENTURE.opener.question}
            </motion.div>
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <FrogAvatar size={420}
                state={speaking ? 'talking' : (thinking || transcribing) ? 'thinking' : 'idle'} />
              <AnimatePresence>{(listening || transcribing) && <ThinkingBubble listening={listening} thinking={transcribing} speaking={speaking} />}</AnimatePresence>
              <AnimatePresence><SpeechBubble text={feedbackText} visible={!!feedbackText} /></AnimatePresence>
            </div>
            <MicButton listening={listening} onClick={handleMic} disabled={false} />
          </div>
        </Scaled>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  // ─── COMPLETION SCREEN ─────────────────────────────
  // ════════════════════════════════════════════════════
  if (phase === 'complete') {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Nunito', sans-serif", padding: 30, position: 'relative' }}>
        <FarmBackground />
        <RotateHint />
        <SessionRecorderButton score={score} />
        <WpLinkGate />
        <div style={{ position: 'fixed', top: 14, left: 20, zIndex: 10 }}><LittleSpongesLogo /></div>
        <Scaled scale={uiScale}>
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          style={{ background: 'rgba(240,240,240,0.95)', borderRadius: 30, padding: '40px 50px',
            border: `4px solid ${BRAND.cyan}`, boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            textAlign: 'center', maxWidth: 500, zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: -10 }}>
            <FrogAvatar size={200} state="correct" />
          </div>
          <h1 style={{ color: BRAND.orange, fontSize: 36, fontWeight: 900, margin: '0 0 8px' }}>
            {score >= Math.ceil(QUESTIONS_PER_GAME * 0.7) ? uiText('greatJob', 'Great Job!') : uiText('goodTry', 'Good Try!')}
          </h1>
          <p style={{ color: BRAND.teal, fontSize: 24, fontWeight: 700, margin: '0 0 16px' }}>
            {typeof PACK_UI.stars === 'function' ? PACK_UI.stars(score, QUESTIONS_PER_GAME) : `You got ${score} out of ${QUESTIONS_PER_GAME} stars!`}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 30, flexWrap: 'wrap' }}>
            {Array.from({ length: QUESTIONS_PER_GAME }, (_, i) => {
              const earned = i < score;
              return (
                <motion.span
                  key={i}
                  initial={earned ? { scale: 0 } : false}
                  animate={{ scale: 1 }}
                  transition={{ delay: i * 0.15 }}
                  style={{
                    fontSize: 32,
                    opacity: earned ? 1 : 0.25,
                    filter: earned ? 'none' : 'grayscale(100%)',
                  }}
                >
                  ⭐
                </motion.span>
              );
            })}
          </div>
          <div style={{ borderTop: '2px solid #ccc', paddingTop: 24, display: 'flex', justifyContent: 'center', gap: 30 }}>
            <RoundButton onClick={handleRestart} color={BRAND.btnGreen} darkColor={BRAND.btnGreenDark} size={80} icon="↻" label={uiText('playAgain', 'Play Again')} />
            <RoundButton onClick={handleExit} color={IS_WP_LINK ? BRAND.red : BRAND.tealLight} darkColor={IS_WP_LINK ? BRAND.redDark : BRAND.teal} size={80} icon={IS_WP_LINK ? '✕' : '🏠'} label={IS_WP_LINK ? 'Exit' : uiText('menu', 'Menu')} />
          </div>
        </motion.div>
        </Scaled>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  // ─── FAVORITE QUESTION SCREEN ──────────────────────
  // ════════════════════════════════════════════════════
  // Also render this screen during the celebrate sub-phase so we don't
  // flash back to the last animal card when the child gives their answer.
  if (phase === 'favorite' || (phase === 'celebrate' && gameOverRef.current)) {
    return (
      <div style={{ height: '100vh', overflow: 'hidden', fontFamily: "'Nunito', sans-serif",
        display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
        <FarmBackground />
        <RotateHint />
        <SessionRecorderButton score={score} />
        <WpLinkGate />
        <SttDebugOverlay transcript={transcript} alternatives={alternatives} listening={listening} />
        <div style={{ position: 'fixed', top: 14, left: 20, zIndex: 10 }}><LittleSpongesLogo /></div>

        <Scaled scale={uiScale}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, zIndex: 2 }}>

          <StarRow score={score} />

          {/* Prompt label above the card — matches the main game layout */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            style={{ fontFamily: "'Nunito', sans-serif", fontSize: 22, fontWeight: 800, color: BRAND.white,
              textShadow: `2px 2px 6px ${BRAND.teal}`, textAlign: 'center' }}>
            {favQuestion()}
          </motion.div>

          {/* Collection card for the favorite question (pack-supplied image) */}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
            style={{ width: 600, height: 450, borderRadius: 28, overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)', border: `6px solid ${BRAND.gold}`, background: BRAND.white }}>
            <img src={favImage()} alt="Choices"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </motion.div>

          <AnimatePresence>{((listening || transcribing) ? partialText : heardText) && phase === 'favorite' && (
            <HeardStrip key={(listening || transcribing) ? 'live' : heardText} text={(listening || transcribing) ? partialText : heardText} expected={favoritePool().flatMap((o) => [o.word, o.plural])} />
          )}</AnimatePresence>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minHeight: 140 }}>
            {phase === 'favorite' && <MicButton listening={listening} onClick={handleMic} disabled={false} />}
            {phase === 'celebrate' && <Fireworks />}
          </div>
        </div>

        {/* Frog — same position + size as the main game */}
        <div style={{ position: 'absolute', top: 'calc(50% - 28px)', left: 'calc(50% + 200px)',
          transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, zIndex: 1 }}>
          <FrogAvatar size={500} state={phase === 'celebrate' ? 'correct' : speaking ? 'talking' : (thinking || transcribing) ? 'thinking' : 'idle'} />
          <AnimatePresence>{(listening || transcribing || thinking) && <ThinkingBubble listening={listening} thinking={transcribing || thinking} speaking={speaking} />}</AnimatePresence>
          <AnimatePresence><SpeechBubble text={feedbackText} visible={!!feedbackText} /></AnimatePresence>
        </div>
        </Scaled>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  // ─── MAIN GAME LAYOUT ─────────────────────────────
  // ════════════════════════════════════════════════════

  // Show which question tier we're on. Uses exercise.plural (proper plurals
  // like "geese", "sheep") and singular vs plural framing based on count, so
  // multi-animal cards say "What are these?" / "What color are the chickens?" /
  // "How many geese do you see?" — matching the audio the frog plays.
  const isMulti = exercise.count > 1;
  const packTierQ = questionTier !== 'favorite' ? packQ(exercise, questionTier) : null;
  const tierLabel = packTierQ ? packTierQ.text
    : questionTier === 'name'
    ? (exercise.ask ? exercise.ask.text : isMulti ? 'What are these?' : 'What is this?')
    : questionTier === 'color' ? (isMulti ? `What color are the ${exercise.plural}?` : `What color is the ${exercise.word}?`)
    : `How many ${exercise.plural} do you see?`;

  return (
    <div style={{ height: '100vh', overflow: 'hidden', fontFamily: "'Nunito', sans-serif",
      display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
      <FarmBackground />
      <RotateHint />
        <SessionRecorderButton score={score} />
        <WpLinkGate />
      <SttDebugOverlay transcript={transcript} alternatives={alternatives} listening={listening} />
      {/* Timing readout is a dev tool — now that AI is the default it must NOT
          show for kids; it rides with ?debug alongside the STT overlay. */}
      {DEBUG_STT && <TimingOverlay />}
      <div style={{ position: 'fixed', top: 14, left: 20, zIndex: 10 }}><LittleSpongesLogo /></div>

      {/* Menu button (🏠 back to the adventure picker); WP lesson embeds keep the red ✕ Exit — there is no picker in the child's world there. */}
      <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
        onClick={handleExit}
        style={{ position: 'fixed', top: 12, right: 12, width: 36, height: 36, borderRadius: '50%',
          border: `3px solid ${IS_WP_LINK ? BRAND.redDark : BRAND.teal}`, background: IS_WP_LINK ? BRAND.red : BRAND.tealLight, color: BRAND.white,
          fontSize: 18, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: '0 3px 10px rgba(0,0,0,0.2)', zIndex: 10 }}>{IS_WP_LINK ? '✕' : '🏠'}</motion.button>

      <Scaled scale={uiScale}>
      {/* Card + controls centered. zIndex 2 (one above the avatar's 1) so
          Mishka's wide celebration paws pass BEHIND the card. */}
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, zIndex: 2 }}>

        <StarRow score={score} />

        {/* Question tier label above card */}
        <motion.div key={tierLabel} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          style={{ fontFamily: "'Nunito', sans-serif", fontSize: 22, fontWeight: 800, color: BRAND.white,
            textShadow: `2px 2px 6px ${BRAND.teal}`, textAlign: 'center' }}>
          {tierLabel}
        </motion.div>

        <AnimatePresence mode="wait">
          {/* Keyed on exercise.id only, so the card animates in once per card
              and stays put while the tier (name → color → count) changes. */}
          <motion.div key={exercise.id}
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.4 }}
            style={{ width: 600, height: 450, borderRadius: 28, overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)', border: `6px solid ${BRAND.gold}`, background: BRAND.white }}>
            <img src={exercise.image} alt={exercise.word}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </motion.div>
        </AnimatePresence>

        <AnimatePresence>{repeatTarget && <RepeatBanner target={repeatTarget} />}</AnimatePresence>

        <AnimatePresence>{((listening || transcribing) ? partialText : heardText) && (
          <HeardStrip key={(listening || transcribing) ? 'live' : heardText} text={(listening || transcribing) ? partialText : heardText}
            expected={[repeatTarget?.sentence || '', exercise.word, exercise.plural, ...(exercise.extraWords || []),
              ...(questionTier === 'color' && exercise.color ? exercise.color.split(/\s+and\s+/) : []),
              ...(questionTier === 'count' ? (NUMBER_WORDS[exercise.count] || []).concat(exercise.numberWord || [], String(exercise.count)) : [])]} />
        )}</AnimatePresence>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minHeight: 140 }}>
          {phase === 'respond' && <MicButton listening={listening} onClick={handleMic} disabled={false} />}
          {phase === 'feedback' && (
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} style={{ fontSize: 50 }}>🌟</motion.div>
          )}
          {phase === 'celebrate' && <Fireworks />}
          {phase === 'giveAnswer' && <div style={{ minHeight: 50 }} />}
        </div>
      </div>

      {/* Frog */}
      <div style={{ position: 'absolute', top: 'calc(50% - 28px)', left: 'calc(50% + 200px)',
        transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, zIndex: 1 }}>
        <FrogAvatar size={500}
          state={phase === 'celebrate' ? 'correct' : phase === 'giveAnswer' ? 'tryagain' : speaking ? 'talking' : (thinking || transcribing) ? 'thinking' : 'idle'} />
        <AnimatePresence>{(listening || transcribing || thinking) && <ThinkingBubble listening={listening} thinking={transcribing || thinking} speaking={speaking} />}</AnimatePresence>
        <AnimatePresence><SpeechBubble text={feedbackText} visible={!!feedbackText} /></AnimatePresence>
      </div>
      </Scaled>
    </div>
  );
}
